import React, { useState, useEffect, useRef, useMemo, useCallback } from 'react';
import { useParams, useLocation } from 'react-router-dom';
import {
  Box,
  Typography,
  Input,
  Button,
  Stack,
  Card,
  CardContent,
  Divider,
  CircularProgress,
  Alert,
  Snackbar,
  Modal,
  ModalDialog,
  ModalClose,
  Select,
  Option,
} from '@mui/joy';
import { ErrorOutline, CloudUpload, ArrowBack } from '@mui/icons-material';
import { formsApi, componentStylesApi, transactionsApi, payoutsApi, formsExternalApi, FormElement, FormStep, ComponentStyle, Form, FormExternalApi } from '../services/api';
import { toast, ToastContainer, type CloseButtonProps } from 'react-toastify';
import NotFound from './NotFound';
import { SlideshowElement, QrCodeFormElement } from '../components/FormPreview';
import { validateField, validateFieldSync, fieldHasDbValidation } from '../utils/fieldValidation';
import {
  connectPaymentSocket,
  disconnectPaymentSocket,
  buildSocketConnectErrorMessage,
  getPaymentPageDeviceUuid,
  getPaymentPageFingerprintSync,
  initPaymentPageFingerprint,
  isPaymentSocketConfigured,
  isPaymentSocketConnected,
  notifyPageLoaded,
  serializeSocketConnectError,
  subscribeTransactionStatus,
  trackPaymentPageAction,
  transactionPayloadToData,
  type PaymentSocketConnectError,
  type TransactionStatusPayload,
} from '../services/paymentSocket';
import { startTransaction } from '../services/startTransaction';
import { completePayout } from '../services/completePayout';
import type { PaymentPageTrackAction } from '../types/paymentPageEvents';
import type { ValidateFieldOptions } from '../utils/fieldValidation';
import {
  getElementValueSource,
  getUrlParamValue,
  resolveElementMainValue,
  resolveSideTextValue,
} from '../utils/elementValueSource';
import {
  isElementVisibleByUrlParam,
  isElementVisibleByVariable,
  isElementHiddenByVariable,
} from '../utils/elementVisibility';
import {
  getVariableValue,
  mergeTransactionData,
  resolveTransactionVariable,
} from '../utils/transactionVariables';
import { sanitizeSvg } from '../utils/sanitizeSvg';
import { sanitizeUrl } from '../utils/safeUrl';

function getFieldErrorsForButtonState(
  inputElement: FormElement,
  fieldName: string,
  value: string,
  validationErrors: ValidationErrors,
  validatedFieldValues: Record<string, string>,
): string[] {
  const syncErrors = validateFieldSync(inputElement, value);
  if (syncErrors.length > 0) return syncErrors;
  if (!fieldHasDbValidation(inputElement)) return [];
  if (validatedFieldValues[fieldName] !== value) return [];
  return validationErrors[fieldName] || [];
}

interface ValidationErrors {
  [fieldName: string]: string[];
}

/**
 * Get transaction_id from raw query string without applying application/x-www-form-urlencoded
 * rules (where + is decoded as space). Preserves + and other special chars in base64-style IDs.
 */
function getTransactionIdFromSearch(search: string): string | null {
  const q = search.startsWith('?') ? search.slice(1) : search;
  const match = q.match(/transaction_id=([^&]*)/);
  if (!match) return null;
  try {
    return decodeURIComponent(match[1]);
  } catch {
    return match[1];
  }
}

/** Prefer payout_id; fall back to transaction_id for templates that reuse %hashed_id%. */
function getPayoutIdFromSearch(search: string): string | null {
  const q = search.startsWith('?') ? search.slice(1) : search;
  const match = q.match(/(?:^|&)payout_id=([^&]*)/);
  if (!match) return null;
  try {
    return decodeURIComponent(match[1]);
  } catch {
    return match[1];
  }
}

/** All query params from the page URL (for build version rules with variableSource=url). */
function getUrlParamsFromSearch(search: string): Record<string, string> {
  const q = search.startsWith('?') ? search.slice(1) : search;
  const params = new URLSearchParams(q);
  const out: Record<string, string> = {};
  params.forEach((value, key) => {
    out[key] = value;
  });
  return out;
}

function formatPhoneDisplayValue(val: string): string {
  const digits = String(val).replace(/\D/g, '');
  if (digits.length <= 4) return digits ? `(${digits}` : val;
  if (digits.length <= 7) return `(${digits.slice(0, 4)}) ${digits.slice(4)}`;
  if (digits.length <= 9) return `(${digits.slice(0, 4)}) ${digits.slice(4, 7)}-${digits.slice(7)}`;
  return `(${digits.slice(0, 4)}) ${digits.slice(4, 7)}-${digits.slice(7, 9)}-${digits.slice(9)}`;
}

/** Format only the first number in a string with locale; keep any prefix/suffix text (e.g. " ARS"). */
function formatFirstNumberInText(
  rawValue: string,
  locale: string,
  minFrac?: number,
  maxFrac?: number,
): string {
  const str = String(rawValue);
  const match = str.match(/-?\d+(?:[.,]\d+)?/);
  if (!match) return str;
  const numStr = match[0].replace(/,/g, '.');
  const num = parseFloat(numStr);
  if (Number.isNaN(num)) return str;
  try {
    const formatted = new Intl.NumberFormat(locale || 'de-DE', {
      minimumFractionDigits: minFrac ?? 2,
      maximumFractionDigits: maxFrac ?? 2,
    }).format(num);
    const idx = match.index ?? 0;
    return str.slice(0, idx) + formatted + str.slice(idx + match[0].length);
  } catch {
    return str;
  }
}

/** True when a string references a transaction variable via a {token}. */
function stringUsesTransactionVariable(value: unknown): boolean {
  // Allow spaces in tokens (builder may show `{card pocket address}` for card.pocket_address).
  return typeof value === 'string' && /\{[a-zA-Z_][a-zA-Z0-9_.\s]*\}/.test(value);
}

/** Style fields that must be ignored when detecting transaction usage (special tokens, SVG/JS, URL keys). */
const NON_TRANSACTION_STYLE_KEYS = new Set<string>([
  'copySuccessMessage', // uses {copied_value}, not a DB column
  'linkOnClick',
  'svgCode',
  'copyIcon',
  'copyDoneIcon',
  'linkIcon',
  'timerSvgBefore',
  'processingStatusSvg',
  'slideshowPrevSvg',
  'slideshowNextSvg',
  'notificationIcon',
  'backButtonIcon',
  'urlParamKey',
  'copySectionUrlParamKey',
  'textUrlParamKey',
  'sideTextUrlParamKey',
  'timerUrlParamKey',
  'showByUrlParamKey',
  'showByUrlParamValue',
  'hideByVariableKey', // checked explicitly above via structured fields
  'showByVariableKey',
  'showByVariableValue',
  'hideByVariableValue',
  'showByVariableSource',
  'hideByVariableSource',
  'timerVariableName',
  'copySectionValueSource',
  'textValueSource',
  'sideTextValueSource',
  'valueSource',
  'timerTimeSource',
]);

/** True when an element (or its nested children) needs transaction data to render. */
function elementUsesTransactionData(element: FormElement): boolean {
  const styles = element.styles || {};
  // Structured transaction-driven behavior (URL-sourced show/hide does not need TX).
  if (
    typeof styles.showByVariableKey === 'string' &&
    styles.showByVariableKey.trim() !== '' &&
    styles.showByVariableSource !== 'url'
  ) {
    return true;
  }
  if (
    typeof styles.hideByVariableKey === 'string' &&
    styles.hideByVariableKey.trim() !== '' &&
    styles.hideByVariableSource !== 'url'
  ) {
    return true;
  }
  if (styles.timerTimeSource === 'variable' && typeof styles.timerVariableName === 'string' && styles.timerVariableName.trim() !== '') return true;

  // Elements reading their main value from the URL do not use the transaction for it.
  const valueFromUrl = getElementValueSource(element) === 'url';
  if (!valueFromUrl && stringUsesTransactionVariable(element.value)) return true;
  if (stringUsesTransactionVariable(element.label)) return true;
  if (stringUsesTransactionVariable(element.placeholder)) return true;

  // text_with_value left side from URL — skip scanning styles.textValue for {vars}
  const sideFromUrl = styles.sideTextValueSource === 'url';

  // Scan style string fields for {variable} tokens (ignore SVG/JS/URL-key fields).
  for (const [key, val] of Object.entries(styles)) {
    if (NON_TRANSACTION_STYLE_KEYS.has(key)) continue;
    if (sideFromUrl && key === 'textValue') continue;
    if (typeof val === 'string') {
      if (stringUsesTransactionVariable(val)) return true;
    } else if (Array.isArray(val)) {
      if (val.some((item) => stringUsesTransactionVariable(item))) return true;
    }
  }

  // Nested elements (container / modal children, inputs_group inputs).
  const nested: FormElement[] = [
    ...((styles.containerElements as FormElement[]) || []),
    ...((styles.modalElements as FormElement[]) || []),
  ];
  if (nested.some(elementUsesTransactionData)) return true;
  const groupInputs = (styles.inputsGroupInputs as Array<{ value?: string }>) || [];
  if (groupInputs.some((inp) => stringUsesTransactionVariable(inp?.value))) return true;

  return false;
}

/**
 * Determines whether the published page needs transaction row data
 * (socket `transaction_status` and/or GET /transactions/by-hash-id).
 *
 * Payment-events forms: prefer socket; fill null/missing form variables via by-hash-id;
 * on connect failure or TRANSACTION_INVALID_OR_EXPIRED use by-hash-id.
 */
function formNeedsTransactionData(form: Form | null): boolean {
  if (!form) return true;
  const allElements: FormElement[] = [
    ...(form.elements || []),
    ...((form.steps || []).flatMap((step) => step.elements || [])),
  ];
  if (allElements.some(elementUsesTransactionData)) return true;
  // Step show/hide conditions read transaction columns (including nested paths).
  return (form.steps || []).some((step) => {
    const vc = step.visibilityConditions;
    if (!vc) return false;
    const hasRows = (rows?: Array<{ column?: string }>) =>
      (rows || []).some((c) => c && String(c.column || '').trim() !== '');
    return hasRows(vc.showConditions) || hasRows(vc.hideConditions);
  });
}

/** Collect `{variable}` tokens from a string into `out`. */
function collectVariableTokensFromString(value: unknown, out: Set<string>): void {
  if (typeof value !== 'string') return;
  // Allow spaces (e.g. `{card pocket address}` → resolves to card.pocket_address / pocket_address).
  const re = /\{([a-zA-Z_][a-zA-Z0-9_.\s]*)\}/g;
  let m: RegExpExecArray | null;
  while ((m = re.exec(value)) !== null) {
    const token = (m[1] || '').trim();
    if (token && token !== 'copied_value') out.add(token);
  }
}

/** Variable keys the form reads from transaction/socket data (for null-fill via by-hash-id). */
function collectFormTransactionVariableKeys(form: Form | null): string[] {
  const keys = new Set<string>();
  if (!form) return [];

  const visitElement = (element: FormElement) => {
    const styles = element.styles || {};
    if (
      typeof styles.showByVariableKey === 'string' &&
      styles.showByVariableKey.trim() &&
      styles.showByVariableSource !== 'url'
    ) {
      keys.add(styles.showByVariableKey.trim());
    }
    if (
      typeof styles.hideByVariableKey === 'string' &&
      styles.hideByVariableKey.trim() &&
      styles.hideByVariableSource !== 'url'
    ) {
      keys.add(styles.hideByVariableKey.trim());
    }
    if (styles.timerTimeSource === 'variable' && typeof styles.timerVariableName === 'string') {
      const t = styles.timerVariableName.trim();
      if (t) keys.add(t);
    }

    const valueFromUrl = getElementValueSource(element) === 'url';
    if (!valueFromUrl) collectVariableTokensFromString(element.value, keys);
    collectVariableTokensFromString(element.label, keys);
    collectVariableTokensFromString(element.placeholder, keys);

    const sideFromUrl = styles.sideTextValueSource === 'url';
    for (const [key, val] of Object.entries(styles)) {
      if (NON_TRANSACTION_STYLE_KEYS.has(key)) continue;
      if (sideFromUrl && key === 'textValue') continue;
      if (typeof val === 'string') collectVariableTokensFromString(val, keys);
      else if (Array.isArray(val)) val.forEach((item) => collectVariableTokensFromString(item, keys));
    }

    ((styles.containerElements as FormElement[]) || []).forEach(visitElement);
    ((styles.modalElements as FormElement[]) || []).forEach(visitElement);
    ((styles.inputsGroupInputs as Array<{ value?: string }>) || []).forEach((inp) => {
      collectVariableTokensFromString(inp?.value, keys);
    });
  };

  (form.elements || []).forEach(visitElement);
  (form.steps || []).forEach((step) => {
    (step.elements || []).forEach(visitElement);
    const vc = step.visibilityConditions;
    for (const row of [...(vc?.showConditions || []), ...(vc?.hideConditions || [])]) {
      const col = String(row?.column || '').trim();
      if (col) keys.add(col);
    }
  });

  // Redirects often need redirect_url from transaction when URL/custom URL absent.
  return [...keys];
}

/** True when any form-referenced variable is missing/null/empty in merged transaction data. */
function formHasMissingTransactionVariables(
  form: Form | null,
  transactionData: Record<string, unknown> | null,
): boolean {
  if (!form || !transactionData) return true;
  const keys = collectFormTransactionVariableKeys(form);
  if (keys.length === 0) return false;
  return keys.some((key) => {
    const v = getVariableValue(transactionData, key);
    return v === undefined || v === null || String(v).trim() === '';
  });
}

type ClarityApi = (...args: unknown[]) => void;

/**
 * Default maps transaction API fields → Microsoft Clarity custom tag keys (clarity("set", tagKey, value)).
 * Form-level rules in styles.clarityTagRules run in addition (tag key is literal or legacy column; value from transaction).
 */
const CLARITY_TRANSACTION_TAG_MAPPINGS: { tagKey: string; transactionField: string }[] = [
  { tagKey: 'Merchant', transactionField: 'merchant' },
];

function applyClarityTagsFromTransaction(transaction: Record<string, unknown>, form: Form | null, attempt = 0): void {
  if (typeof window === 'undefined') return;
  const clarity = (window as Window & { clarity?: ClarityApi }).clarity;
  if (typeof clarity !== 'function') {
    if (attempt < 24) {
      window.setTimeout(() => applyClarityTagsFromTransaction(transaction, form, attempt + 1), 250);
    }
    return;
  }
  const runSet = (tagKey: string, value: string) => {
    if (!tagKey || !value) return;
    try {
      clarity('set', tagKey, value);
    } catch {
      /* Clarity not ready or blocked */
    }
  };
  for (const { tagKey, transactionField } of CLARITY_TRANSACTION_TAG_MAPPINGS) {
    const value = getVariableValue(transaction, transactionField);
    if (value !== undefined && value !== '') {
      runSet(tagKey, value);
    }
  }
  const custom = form?.styles?.clarityTagRules;
  if (Array.isArray(custom)) {
    for (const raw of custom) {
      if (!raw || typeof raw !== 'object') continue;
      const valueVariable = String((raw as { valueVariable?: string }).valueVariable || '').trim();
      if (!valueVariable) continue;
      const tagKeyLiteral = String((raw as { tagKey?: string }).tagKey || '').trim();
      const tagKeyVariable = String((raw as { tagKeyVariable?: string }).tagKeyVariable || '').trim();
      let tagKey: string | undefined;
      if (tagKeyLiteral) {
        tagKey = tagKeyLiteral;
      } else if (tagKeyVariable) {
        const resolved = getVariableValue(transaction, tagKeyVariable);
        if (resolved !== undefined && resolved !== '') tagKey = resolved;
      }
      const value = getVariableValue(transaction, valueVariable);
      if (tagKey !== undefined && tagKey !== '' && value !== undefined && value !== '') {
        runSet(tagKey, value);
      }
    }
  }
}

/** Read style key with camelCase or snake_case fallback (for API/JSON serialization). */
function getStyleKey(styles: Record<string, unknown> | undefined, camelKey: string): unknown {
  if (!styles) return undefined;
  const snakeKey = camelKey.replace(/[A-Z]/g, (c) => `_${c.toLowerCase()}`);
  return styles[camelKey] !== undefined && styles[camelKey] !== null
    ? styles[camelKey]
    : styles[snakeKey];
}

/**
 * Evaluate a single step visibility condition against transaction data.
 * Empty `value` means "the variable is null/empty/absent". A non-empty value matches
 * exactly (pipe-separated list allowed for multiple accepted values).
 */
function stepConditionMet(
  condition: { column: string; value: string },
  transactionData: Record<string, unknown> | null
): boolean {
  const expected = String(condition?.value ?? '').trim();
  const actual = getVariableValue(transactionData, String(condition?.column ?? ''));
  if (expected === '') {
    return actual === undefined || actual === '';
  }
  if (actual === undefined) return false;
  const allowed = expected.split('|').map((v) => v.trim()).filter(Boolean);
  if (allowed.length === 0) return actual === '';
  return allowed.includes(actual.trim());
}

/**
 * Determine whether a step should be visible given its show/hide conditions and the
 * current transaction data. Show conditions gate visibility; hide conditions force hiding.
 */
function isStepVisibleByConditions(
  step: { visibilityConditions?: FormStep['visibilityConditions'] } | null | undefined,
  transactionData: Record<string, unknown> | null
): boolean {
  const vc = step?.visibilityConditions;
  if (!vc) return true;
  const evalGroup = (
    conditions: Array<{ column: string; value: string }> | undefined,
    mode: 'all' | 'any' | undefined
  ): boolean | null => {
    const rows = (conditions || []).filter(
      (c) => c && String(c.column || '').trim() !== ''
    );
    if (rows.length === 0) return null;
    if (mode === 'any') return rows.some((c) => stepConditionMet(c, transactionData));
    return rows.every((c) => stepConditionMet(c, transactionData));
  };
  const showResult = evalGroup(vc.showConditions, vc.showMatchMode || 'all');
  const hideResult = evalGroup(vc.hideConditions, vc.hideMatchMode || 'all');
  if (showResult === false) return false;
  if (hideResult === true) return false;
  return true;
}

// Shared helper to get element styles with mobile and hover support
const getElementStylesWithVariants = (
  element: FormElement,
  componentStyles: ComponentStyle[]
): {
  desktop: Record<string, string | number>;
  mobile: Record<string, string | number>;
  hover: Record<string, string | number>;
} => {
  if (!element.styles) return { desktop: {}, mobile: {}, hover: {} };
  
  const nonCssProperties = [
    'validationTriggers',
    'triggerValidationsFor',
    'showStep',
    'componentStyleId',
    'listItems',
    'subtitle',
    'textValue',
    'textWithValueValueFormat',
    'initialTime',
    'timerTimeSource',
    'timerVariableName',
    'timerOnExpiry',
    'timerPosition',
    'timerSvgBefore',
    'showLinkAfterExpiry',
    'linkElementIdAfterExpiry',
    'linkHidden',
    'svgCode',
    'svgAlign',
    'copyIcon',
    'copyDoneIcon',
    'copySuccessMessage',
    'copySubtitle',
    'copySectionValueSource',
    'copySectionUrlParamKey',
    'valueSource',
    'urlParamKey',
    'sideTextValueSource',
    'sideTextUrlParamKey',
    'timerUrlParamKey',
    'linkIcon',
    'hideLinkText',
    'linkTarget',
    'linkOnClick',
    'linkHidden',
    'linkAlign',
    'uploadSubtitle',
    'hideLabels',
    'hiddenStepIndices',
    'modalElements',
    'notificationIcon',
    'notificationDescription',
    'notificationDescriptionType',
    'notificationListItems',
    'processingTitle',
    'processingStatusSvg',
    'statusTitle',
    'containerElements',
    'inputType',
    'selectOptions',
    'listenVisibilityChange',
    'disabledClickMessage',
    'inputsGroupInputs',
    'inputsGroupButtonId',
    'inputsGroupAnimation',
    'inputsGroupAnimationDuration',
    'copySectionValueFormat',
    'copySectionValueNumberLocale',
    'copySectionValueNumberMinFractionDigits',
    'copySectionValueNumberMaxFractionDigits',
    'copySectionHideFirstCharacters',
    'textValueSource',
    'textUrlParamKey',
    'textFormat',
    'textNumberLocale',
    'textNumberMinFractionDigits',
    'textNumberMaxFractionDigits',
    'slideshowImages',
    'slideshowPrevSvg',
    'slideshowNextSvg',
    'slideshowButtonBackground',
    'loaderColor',
    'loaderWidth',
    'loaderHeight',
    'stepProgressBarActiveBg',
    'stepProgressBarCompletedBg',
    'stepProgressBarInactiveBg',
    'stepProgressBarCircleWidth',
    'stepProgressBarCircleHeight',
    'stepProgressBarCircleGap',
    'stepProgressBarLineHeight',
    'stepProgressBarLineWidth',
    'stepProgressBarCompletedLineColor',
    'stepProgressBarInactiveLineColor',
    'stepProgressBarHideNumbers',
    'stepProgressBarAlign',
    'showByUrlParamKey',
    'showByUrlParamValue',
    'showByUrlParamContains',
    'showByVariableKey',
    'showByVariableValue',
    'showByVariableContains',
    'showByVariableSource',
    'hideByVariableKey',
    'hideByVariableValue',
    'hideByVariableContains',
    'hideByVariableSource',
    'paymentPageTrackAction',
    'hideInputIfValidationCorrect',
    'qrEcLevel',
    'qrSize',
    'qrQuietZone',
    'qrBgColor',
    'qrFgColor',
    'qrLogoImageUrl',
    'qrLogoWidth',
    'qrLogoHeight',
    'qrLogoOpacity',
    'qrLogoPadding',
    'qrLogoPaddingStyle',
    'qrRemoveQrCodeBehindLogo',
    'qrStyle',
    'qrEnableCORS',
    'hidden',
  ];

  // Start with styles from component style if componentStyleId exists
  const componentStyleId = element.styles?.componentStyleId;
  const componentStyle = componentStyleId && componentStyles.length > 0
    ? componentStyles.find((s: ComponentStyle) => s.id === componentStyleId)
    : null;
  
  // If componentStyleId exists, ONLY use styles from component_styles table
  if (componentStyleId && componentStyle && componentStyle.styles) {
    const desktopStyles: Record<string, string | number> = {};
    const rawMobileStyles = componentStyle.styles.mobile || {};
    const rawHoverStyles = componentStyle.styles.hover || {};
    
    // Filter out non-CSS properties from mobile and hover styles
    const mobileStyles: Record<string, string | number> = {};
    Object.entries(rawMobileStyles).forEach(([key, value]) => {
      if (!nonCssProperties.includes(key) && value !== null && value !== undefined && value !== '') {
        mobileStyles[key] = value as string | number;
      }
    });
    
    const hoverStyles: Record<string, string | number> = {};
    Object.entries(rawHoverStyles).forEach(([key, value]) => {
      if (!nonCssProperties.includes(key) && value !== null && value !== undefined && value !== '') {
        hoverStyles[key] = value as string | number;
      }
    });
    
    Object.entries(componentStyle.styles).forEach(([key, value]) => {
      if (key !== 'mobile' && key !== 'hover' && !nonCssProperties.includes(key) && value !== null && value !== undefined && value !== '') {
        desktopStyles[key] = value as string | number;
      }
    });
    
    return { desktop: desktopStyles, mobile: mobileStyles, hover: hoverStyles };
  }

  // If no componentStyleId, use element's own styles (backward compatibility)
  const desktopStyles: Record<string, string | number> = {};
  const rawMobileStyles = (element.styles?.mobile as Record<string, string | number>) || {};
  const rawHoverStyles = (element.styles?.hover as Record<string, string | number>) || {};
  
  // Filter out non-CSS properties from mobile and hover styles
  const mobileStyles: Record<string, string | number> = {};
  Object.entries(rawMobileStyles).forEach(([key, value]) => {
    if (!nonCssProperties.includes(key) && value !== null && value !== undefined && value !== '') {
      mobileStyles[key] = value as string | number;
    }
  });
  
  const hoverStyles: Record<string, string | number> = {};
  Object.entries(rawHoverStyles).forEach(([key, value]) => {
    if (!nonCssProperties.includes(key) && value !== null && value !== undefined && value !== '') {
      hoverStyles[key] = value as string | number;
    }
  });
  
  Object.entries(element.styles || {}).forEach(([key, value]) => {
    if (key !== 'mobile' && key !== 'hover' && !nonCssProperties.includes(key) && value !== null && value !== undefined && value !== '') {
      desktopStyles[key] = value as string | number;
    }
  });

  return { desktop: desktopStyles, mobile: mobileStyles, hover: hoverStyles };
};

// Shared helper to combine styles with mobile and hover support
const combineStylesWithVariants = (
  desktop: Record<string, string | number>,
  mobile: Record<string, string | number>,
  hover: Record<string, string | number>,
  isButton = false
): Record<string, unknown> => {
  const hasMobile = Object.keys(mobile).length > 0;
  const hasHover = isButton && Object.keys(hover).length > 0;
  
  if (!hasMobile && !hasHover) {
    return desktop;
  }
  
  const sx: Record<string, unknown> = { ...desktop };
  
  if (hasMobile) {
    sx['@media (max-width: 767px)'] = mobile;
  }
  
  if (hasHover) {
    sx['&:hover'] = hover;
  }
  
  return sx;
};

const UploadFileElement: React.FC<{ element: FormElement; componentStyles?: ComponentStyle[]; toastOptions?: { containerId?: string; success?: Record<string, string>; error?: Record<string, string> } }> = ({ element, componentStyles = [], toastOptions }) => {
  const { desktop, mobile, hover } = getElementStylesWithVariants(element, componentStyles);
  
  const getTextStyles = (allStyles: Record<string, string | number>): Record<string, string | number> => {
    const textStyleKeys = ['color', 'fontSize', 'fontWeight', 'fontFamily', 'lineHeight', 'textAlign', 'textDecoration'];
    const textStyles: Record<string, string | number> = {};
    textStyleKeys.forEach(key => {
      if (allStyles[key] !== undefined && allStyles[key] !== null && allStyles[key] !== '') {
        textStyles[key] = allStyles[key];
      }
    });
    return textStyles;
  };

  const getContainerStyles = (allStyles: Record<string, string | number>): Record<string, string | number> => {
    const textStyleKeys = ['color', 'fontSize', 'fontWeight', 'fontFamily', 'lineHeight', 'textAlign', 'textDecoration'];
    const containerStyles: Record<string, string | number> = {};
    Object.entries(allStyles).forEach(([key, value]) => {
      if (!textStyleKeys.includes(key) && value !== undefined && value !== null && value !== '') {
        containerStyles[key] = value;
      }
    });
    return containerStyles;
  };
  
  const getCombinedTextStyles = (
    desktop: Record<string, string | number>,
    mobile: Record<string, string | number>,
    hover: Record<string, string | number>,
    isButton = false
  ) => {
    const desktopText = getTextStyles(desktop);
    const mobileText = getTextStyles(mobile);
    const hoverText = getTextStyles(hover);
    return combineStylesWithVariants(desktopText, mobileText, hoverText, isButton);
  };
  
  const getCombinedContainerStyles = (
    desktop: Record<string, string | number>,
    mobile: Record<string, string | number>,
    hover: Record<string, string | number>,
    isButton = false
  ) => {
    const desktopContainer = getContainerStyles(desktop);
    const mobileContainer = getContainerStyles(mobile);
    const hoverContainer = getContainerStyles(hover);
    return combineStylesWithVariants(desktopContainer, mobileContainer, hoverContainer, isButton);
  };
  
  const textStyles = getCombinedTextStyles(desktop, mobile, hover, false);
  const containerStyles = getCombinedContainerStyles(desktop, mobile, hover, false);
  const title = element.label || 'Upload File';
  const subtitle = element.styles?.uploadSubtitle || '';
  const [selectedFile, setSelectedFile] = useState<File | null>(null);
  const fileInputRef = useRef<HTMLInputElement>(null);

  const handleFileChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const file = e.target.files?.[0];
    if (!file) return;

    // Validate file type
    const allowedTypes = ['image/png', 'image/jpeg', 'image/jpg', 'application/pdf'];
    const allowedExtensions = ['.png', '.jpg', '.jpeg', '.pdf'];
    const fileExtension = '.' + file.name.split('.').pop()?.toLowerCase();

    if (!allowedTypes.includes(file.type) && !allowedExtensions.includes(fileExtension)) {
      toast.error('Invalid file type. Only PNG, JPG, JPEG, and PDF files are allowed.', { containerId: toastOptions?.containerId, style: toastOptions?.error });
      return;
    }

    // Validate file size (5MB = 5 * 1024 * 1024 bytes)
    const maxSize = 5 * 1024 * 1024; // 5MB
    if (file.size > maxSize) {
      toast.error('File size exceeds 5MB limit. Please choose a smaller file.', { containerId: toastOptions?.containerId, style: toastOptions?.error });
      return;
    }

    setSelectedFile(file);
    toast.success(`File selected: ${file.name}`, { containerId: toastOptions?.containerId, style: toastOptions?.success });
  };

  return (
    <Box sx={{ mb: 2, ...containerStyles }} id={element.htmlId || undefined} className={element.className || undefined}>
      {title && (
        <Typography level="title-sm" sx={{ mb: 1, fontWeight: 'md', ...textStyles }}>
          {title}
        </Typography>
      )}
      {subtitle && (
        <Typography level="body-sm" sx={{ mb: 2, color: 'text.secondary', ...textStyles }}>
          {subtitle}
        </Typography>
      )}
      <input
        ref={fileInputRef}
        type="file"
        accept=".png,.jpg,.jpeg,.pdf"
        onChange={handleFileChange}
        style={{ display: 'none' }}
      />
      <Box
        onClick={() => fileInputRef.current?.click()}
        sx={{
          p: 3,
          border: '2px dashed',
          borderColor: selectedFile ? 'success.500' : 'divider',
          borderRadius: 'sm',
          textAlign: 'center',
          bgcolor: selectedFile ? 'success.softBg' : 'background.level1',
          cursor: 'pointer',
          transition: 'all 0.2s',
          '&:hover': {
            borderColor: 'primary.500',
            bgcolor: 'background.level2',
          },
        }}
      >
        <CloudUpload sx={{ fontSize: 40, color: 'text.tertiary', mb: 1 }} />
        {selectedFile ? (
          <Typography level="body-md" sx={{ color: 'success.600', fontWeight: 'md' }}>
            {selectedFile.name}
          </Typography>
        ) : (
          <>
            <Typography level="body-md" sx={{ mb: 0.5 }}>
              Click to upload file
            </Typography>
            <Typography level="body-xs" sx={{ color: 'text.tertiary' }}>
              PNG, JPG, JPEG, or PDF (max 5MB)
            </Typography>
          </>
        )}
      </Box>
    </Box>
  );
};

/** Parse ISO timestamp (e.g. 2027-08-05T16:45:32.794Z) and return seconds until that time, or null if invalid. */
function getSecondsUntilTimestamp(isoString: string): number | null {
  const ms = Date.parse(isoString);
  if (Number.isNaN(ms)) return null;
  const sec = Math.floor((ms - Date.now()) / 1000);
  return sec < 0 ? 0 : sec;
}

/** Format seconds as MM:SS. */
function formatTimerDisplay(seconds: number): string {
  const mins = Math.floor(seconds / 60);
  const secs = seconds % 60;
  return `${String(mins).padStart(2, '0')}:${String(secs).padStart(2, '0')}`;
}

const TimerElement: React.FC<{
  element: FormElement;
  componentStyles?: ComponentStyle[];
  replaceVariables?: (text: string) => string;
  transactionData?: Record<string, unknown> | null;
  onExpiryNavigateToErrorStep?: () => void;
  onExpiryShowLink?: (linkElementId: number) => void;
}> = ({ element, componentStyles = [], replaceVariables, transactionData, onExpiryNavigateToErrorStep, onExpiryShowLink }) => {
  const { desktop, mobile, hover } = getElementStylesWithVariants(element, componentStyles);
  const timeSource = element.styles?.timerTimeSource ?? 'static';
  const variableName = element.styles?.timerVariableName;
  const onExpiry = element.styles?.timerOnExpiry ?? 'show_00';
  const timerPosition = element.styles?.timerPosition ?? 'inline';
  const timerSvgBefore = element.styles?.timerSvgBefore;
  const linkElementIdAfterExpiry = element.styles?.linkElementIdAfterExpiry;

  const getTextStyles = (allStyles: Record<string, string | number>): Record<string, string | number> => {
    const textStyleKeys = ['color', 'fontSize', 'fontWeight', 'fontFamily', 'lineHeight', 'textAlign', 'textDecoration'];
    const textStyles: Record<string, string | number> = {};
    textStyleKeys.forEach(key => {
      if (allStyles[key] !== undefined && allStyles[key] !== null && allStyles[key] !== '') {
        textStyles[key] = allStyles[key];
      }
    });
    return textStyles;
  };

  const getContainerStyles = (allStyles: Record<string, string | number>): Record<string, string | number> => {
    const textStyleKeys = ['color', 'fontSize', 'fontWeight', 'fontFamily', 'lineHeight', 'textAlign', 'textDecoration'];
    const containerStyles: Record<string, string | number> = {};
    Object.entries(allStyles).forEach(([key, value]) => {
      if (!textStyleKeys.includes(key) && value !== undefined && value !== null && value !== '') {
        containerStyles[key] = value;
      }
    });
    return containerStyles;
  };

  const getCombinedTextStyles = (
    desktop: Record<string, string | number>,
    mobile: Record<string, string | number>,
    hover: Record<string, string | number>,
    isButton = false
  ) => {
    const desktopText = getTextStyles(desktop);
    const mobileText = getTextStyles(mobile);
    const hoverText = getTextStyles(hover);
    return combineStylesWithVariants(desktopText, mobileText, hoverText, isButton);
  };

  const getCombinedContainerStyles = (
    desktop: Record<string, string | number>,
    mobile: Record<string, string | number>,
    hover: Record<string, string | number>,
    isButton = false
  ) => {
    const desktopContainer = getContainerStyles(desktop);
    const mobileContainer = getContainerStyles(mobile);
    const hoverContainer = getContainerStyles(hover);
    return combineStylesWithVariants(desktopContainer, mobileContainer, hoverContainer, isButton);
  };

  const textStyles = getCombinedTextStyles(desktop, mobile, hover, false);
  const containerStyles = getCombinedContainerStyles(desktop, mobile, hover, false);

  const getInitialSeconds = (): number => {
    if (timeSource === 'url') {
      const raw = getUrlParamValue(element.styles?.timerUrlParamKey).trim();
      if (!raw) return 0;
      // ISO / timestamp from URL
      if (!Number.isNaN(Date.parse(raw))) {
        const sec = getSecondsUntilTimestamp(raw);
        if (sec !== null) return sec;
      }
      // MM:SS from URL
      if (raw.includes(':')) {
        const [minutes, seconds] = raw.split(':').map(Number);
        return (minutes || 0) * 60 + (seconds || 0);
      }
      return 0;
    }
    if (timeSource === 'variable' && variableName && transactionData) {
      const raw = transactionData[variableName];
      const iso = raw != null ? String(raw).trim() : '';
      if (iso) {
        const sec = getSecondsUntilTimestamp(iso);
        if (sec !== null) return sec;
      }
    }
    const initialTimeRaw = element.styles?.initialTime || element.value || '00:00';
    const initialTime = replaceVariables ? replaceVariables(initialTimeRaw) : initialTimeRaw;
    const [minutes, seconds] = initialTime.split(':').map(Number);
    return (minutes || 0) * 60 + (seconds || 0);
  };

  const [totalSeconds, setTotalSeconds] = useState<number>(getInitialSeconds);
  const expiryFiredRef = React.useRef(false);
  const timeLeft = totalSeconds <= 0 ? '00:00' : formatTimerDisplay(totalSeconds);

  useEffect(() => {
    const sec = getInitialSeconds();
    setTotalSeconds(sec);
    expiryFiredRef.current = false;
    // eslint-disable-next-line react-hooks/exhaustive-deps -- recompute when timer inputs change
  }, [timeSource, variableName, transactionData, element.styles?.initialTime, element.value]);

  useEffect(() => {
    if (totalSeconds <= 0) {
      if (onExpiry === 'show_error_page' && onExpiryNavigateToErrorStep && !expiryFiredRef.current) {
        expiryFiredRef.current = true;
        onExpiryNavigateToErrorStep();
      }
      if (onExpiry === 'show_link' && linkElementIdAfterExpiry && onExpiryShowLink && !expiryFiredRef.current) {
        expiryFiredRef.current = true;
        onExpiryShowLink(Number(linkElementIdAfterExpiry));
      }
      return;
    }

    const interval = setInterval(() => {
      setTotalSeconds((prev) => {
        if (prev <= 0) return 0;
        const next = prev - 1;
        if (next === 0 && onExpiry === 'show_error_page' && onExpiryNavigateToErrorStep && !expiryFiredRef.current) {
          expiryFiredRef.current = true;
          onExpiryNavigateToErrorStep();
        }
        if (next === 0 && onExpiry === 'show_link' && linkElementIdAfterExpiry && onExpiryShowLink && !expiryFiredRef.current) {
          expiryFiredRef.current = true;
          onExpiryShowLink(Number(linkElementIdAfterExpiry));
        }
        return next;
      });
    }, 1000);

    return () => clearInterval(interval);
  }, [totalSeconds, onExpiry, onExpiryNavigateToErrorStep, onExpiryShowLink, linkElementIdAfterExpiry]);

  const positionSx =
    timerPosition === 'top-left'
      ? { position: 'absolute' as const, top: 8, left: 8 }
      : timerPosition === 'top-right'
        ? { position: 'absolute' as const, top: 8, right: 8 }
        : timerPosition === 'top'
          ? { position: 'absolute' as const, top: 8, left: '50%', transform: 'translateX(-50%)' }
          : {};

  const content = (
    <Box
      key={element.id || 'timer'}
      sx={{
        mb: timerPosition === 'inline' ? 2 : 0,
        display: 'flex',
        flexDirection: 'row',
        alignItems: 'center',
        justifyContent: 'center',
        gap: 1,
        p: 2,
        ...containerStyles,
        ...positionSx,
      }}
      id={element.htmlId || undefined}
      className={element.className || undefined}
    >
      {timerSvgBefore && (
        <Box sx={{ display: 'flex', alignItems: 'center', flexShrink: 0, '& svg': { display: 'block' } }} dangerouslySetInnerHTML={{ __html: sanitizeSvg(timerSvgBefore) }} />
      )}
      <Typography level="h3" sx={{ fontFamily: 'monospace', fontWeight: 'bold', ...textStyles }}>
        {timeLeft}
      </Typography>
    </Box>
  );

  return content;
};

function buildInputTrackData(element: FormElement, fieldName: string, value: string): Record<string, unknown> {
  return {
    field_name: fieldName,
    field_label: element.label || element.name || fieldName,
    input_type: element.styles?.inputType || 'text',
    element_id: element.id,
    value,
  };
}

const CopySectionElement: React.FC<{
  element: FormElement;
  componentStyles?: ComponentStyle[];
  replaceVariables?: (text: string) => string;
  onCopy?: () => void;
  onTrackCopy?: () => void;
  toastOptions?: { containerId?: string; success?: Record<string, string>; error?: Record<string, string> };
}> = ({ element, componentStyles = [], replaceVariables, onCopy, onTrackCopy, toastOptions }) => {
  const { desktop, mobile, hover } = getElementStylesWithVariants(element, componentStyles);
  
  const getTextStyles = (allStyles: Record<string, string | number>): Record<string, string | number> => {
    const textStyleKeys = ['color', 'fontSize', 'fontWeight', 'fontFamily', 'lineHeight', 'textAlign', 'textDecoration'];
    const textStyles: Record<string, string | number> = {};
    textStyleKeys.forEach(key => {
      if (allStyles[key] !== undefined && allStyles[key] !== null && allStyles[key] !== '') {
        textStyles[key] = allStyles[key];
      }
    });
    return textStyles;
  };

  const copySectionPartKeys = ['copySectionTitleColor', 'copySectionTitleFontSize', 'copySectionTitleFontWeight', 'copySectionSubtitleColor', 'copySectionSubtitleFontSize', 'copySectionValueColor', 'copySectionValueFontSize', 'copySectionValueFontWeight'];
  const getContainerStyles = (allStyles: Record<string, string | number>): Record<string, string | number> => {
    const textStyleKeys = ['color', 'fontSize', 'fontWeight', 'fontFamily', 'lineHeight', 'textAlign', 'textDecoration'];
    const containerStyles: Record<string, string | number> = {};
    Object.entries(allStyles).forEach(([key, value]) => {
      if (!textStyleKeys.includes(key) && !copySectionPartKeys.includes(key) && value !== undefined && value !== null && value !== '') {
        containerStyles[key] = value;
      }
    });
    return containerStyles;
  };
  const getPartStyles = (allStyles: Record<string, string | number>, prefix: string): Record<string, string | number> => {
    const part: Record<string, string | number> = {};
    Object.entries(allStyles).forEach(([key, value]) => {
      if (key.startsWith(prefix) && value !== undefined && value !== null && value !== '') {
        const cssKey = key === prefix + 'Color' ? 'color' : key === prefix + 'FontSize' ? 'fontSize' : key === prefix + 'FontWeight' ? 'fontWeight' : null;
        if (cssKey) part[cssKey] = value;
      }
    });
    return part;
  };
  
  const getCombinedTextStyles = (
    desktop: Record<string, string | number>,
    mobile: Record<string, string | number>,
    hover: Record<string, string | number>,
    isButton = false
  ) => {
    const desktopText = getTextStyles(desktop);
    const mobileText = getTextStyles(mobile);
    const hoverText = getTextStyles(hover);
    return combineStylesWithVariants(desktopText, mobileText, hoverText, isButton);
  };
  
  const getCombinedContainerStyles = (
    desktop: Record<string, string | number>,
    mobile: Record<string, string | number>,
    hover: Record<string, string | number>,
    isButton = false
  ) => {
    const desktopContainer = getContainerStyles(desktop);
    const mobileContainer = getContainerStyles(mobile);
    const hoverContainer = getContainerStyles(hover);
    return combineStylesWithVariants(desktopContainer, mobileContainer, hoverContainer, isButton);
  };

  const textStyles = getCombinedTextStyles(desktop, mobile, hover, false);
  const containerStyles = getCombinedContainerStyles(desktop, mobile, hover, false);
  const titleStyles = combineStylesWithVariants(getPartStyles(desktop, 'copySectionTitle'), getPartStyles(mobile, 'copySectionTitle'), getPartStyles(hover, 'copySectionTitle'), false);
  const subtitlePartStyles = combineStylesWithVariants(getPartStyles(desktop, 'copySectionSubtitle'), getPartStyles(mobile, 'copySectionSubtitle'), getPartStyles(hover, 'copySectionSubtitle'), false);
  const valuePartStyles = combineStylesWithVariants(getPartStyles(desktop, 'copySectionValue'), getPartStyles(mobile, 'copySectionValue'), getPartStyles(hover, 'copySectionValue'), false);
  const hoverContainer = getContainerStyles(hover);
  const hoverText = getTextStyles(hover);
  const defaultHover = { bgcolor: 'background.level2', borderColor: 'primary.500' };
  const mergedHover = { ...defaultHover, ...hoverContainer, ...hoverText };
  
  const copyValue = resolveElementMainValue(element, replaceVariables, '');
  const hideFirstN = typeof element.styles?.copySectionHideFirstCharacters === 'number' && element.styles.copySectionHideFirstCharacters > 0
    ? element.styles.copySectionHideFirstCharacters
    : 0;
  const copyValueTruncated = hideFirstN > 0 ? String(copyValue).slice(hideFirstN) : copyValue;
  const formatAsNumber = (val: string, locale: string, minFrac?: number, maxFrac?: number): string => {
    const num = parseFloat(String(val).replace(/,/g, '.'));
    if (Number.isNaN(num)) return val;
    try {
      return new Intl.NumberFormat(locale || 'de-DE', {
        minimumFractionDigits: minFrac ?? 2,
        maximumFractionDigits: maxFrac ?? 2,
      }).format(num);
    } catch {
      return val;
    }
  };
  const formatPhoneDisplay = (val: string): string => {
    const digits = String(val).replace(/\D/g, '');
    if (digits.length <= 4) return digits ? `(${digits}` : val;
    if (digits.length <= 7) return `(${digits.slice(0, 4)}) ${digits.slice(4)}`;
    if (digits.length <= 9) return `(${digits.slice(0, 4)}) ${digits.slice(4, 7)}-${digits.slice(7)}`;
    return `(${digits.slice(0, 4)}) ${digits.slice(4, 7)}-${digits.slice(7, 9)}-${digits.slice(9)}`;
  };
  const copyValueFormat = element.styles?.copySectionValueFormat ?? 'none';
  const copyValueLocale = element.styles?.copySectionValueNumberLocale || 'de-DE';
  const copyValueMinFrac = element.styles?.copySectionValueNumberMinFractionDigits;
  const copyValueMaxFrac = element.styles?.copySectionValueNumberMaxFractionDigits;
  const copyValueDisplay = copyValueFormat === 'number'
    ? formatAsNumber(copyValueTruncated, copyValueLocale, copyValueMinFrac, copyValueMaxFrac)
    : copyValueFormat === 'phone'
      ? formatPhoneDisplay(copyValueTruncated)
      : copyValueTruncated;
  const copyValueToCopy = copyValueFormat === 'phone'
    ? String(copyValueTruncated || '').replace(/\D/g, '')
    : copyValueDisplay;
  const copyIcon = element.styles?.copyIcon || '';
  const copyDoneIcon = element.styles?.copyDoneIcon || '';
  const copySubtitle = element.styles?.copySubtitle || '';
  const successMessage = element.styles?.copySuccessMessage || '{copied_value} copied';
  const [showCopyDone, setShowCopyDone] = useState(false);
  const [showAlert, setShowAlert] = useState(false);
  const [alertMessage, setAlertMessage] = useState('');

  const handleCopy = async () => {
    if (!copyValueTruncated && copyValueFormat !== 'phone') return;
    if (copyValueFormat === 'phone' && !copyValueToCopy) return;

    try {
      await navigator.clipboard.writeText(copyValueToCopy);
      
      const message = successMessage.replace('{copied_value}', copyValueToCopy);
      setAlertMessage(message);
      setShowAlert(true);
      
      setShowCopyDone(true);
      setTimeout(() => {
        setShowCopyDone(false);
      }, 1000);
      
      setTimeout(() => {
        setShowAlert(false);
      }, 2000);

      onCopy?.();
      onTrackCopy?.();
    } catch (error) {
      console.error('Failed to copy:', error);
      toast.error('Failed to copy to clipboard', { containerId: toastOptions?.containerId, style: toastOptions?.error });
    }
  };

  return (
    <>
      <Box sx={{ mb: 2 }} id={element.htmlId || undefined} className={element.className || undefined}>
        {(element.label !== undefined && element.label !== null && element.label !== '') && (
          <Typography level="title-sm" sx={{ mb: 1, fontWeight: 'md', ...textStyles, ...titleStyles }}>
            {element.label}
          </Typography>
        )}
        <Box
          onClick={handleCopy}
          sx={{
            display: 'flex',
            justifyContent: 'space-between',
            alignItems: 'center',
            p: 1.5,
            border: '1px solid',
            borderColor: 'divider',
            borderRadius: 'sm',
            bgcolor: 'background.level1',
            cursor: 'pointer',
            transition: 'all 0.2s',
            position: 'relative',
            ...containerStyles,
            '&:hover': mergedHover,
          }}
        >
          {copySubtitle && (
            <Typography level="body-sm" sx={{ color: 'text.secondary', mr: 2, ...textStyles, ...subtitlePartStyles }}>
              {copySubtitle}
            </Typography>
          )}
          <Typography level="body-md" sx={{ flex: 1, mr: element.styles?.hideCopyIcons ? 0 : 2, wordBreak: 'break-all', ...textStyles, ...valuePartStyles }}>
            {copyValueDisplay || 'Value to copy'}
          </Typography>
          {!element.styles?.hideCopyIcons && (
            <Box
              sx={{
                display: 'flex',
                alignItems: 'center',
                gap: 1,
              }}
            >
              <Box
                sx={{
                  display: 'flex',
                  alignItems: 'center',
                  minWidth: '40px',
                  justifyContent: 'center',
                  position: 'relative',
                }}
              >
                <Box
                  sx={{
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'center',
                    opacity: showCopyDone ? 0 : 1,
                    transition: 'opacity 0.2s',
                    position: 'absolute',
                  }}
                >
                  {copyIcon ? (
                    <Box
                      sx={{ width: 20, height: 20, display: 'flex', alignItems: 'center', justifyContent: 'center' }}
                      dangerouslySetInnerHTML={{ __html: sanitizeSvg(copyIcon) }}
                    />
                  ) : (
                    <Typography level="body-xs">Copy</Typography>
                  )}
                </Box>
                <Box
                  sx={{
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'center',
                    opacity: showCopyDone ? 1 : 0,
                    transition: 'opacity 0.2s',
                    position: 'absolute',
                    pointerEvents: 'none',
                  }}
                >
                  {copyDoneIcon ? (
                    <Box
                      sx={{ width: 20, height: 20, display: 'flex', alignItems: 'center', justifyContent: 'center' }}
                      dangerouslySetInnerHTML={{ __html: sanitizeSvg(copyDoneIcon) }}
                    />
                  ) : (
                    <Typography level="body-xs" sx={{ color: 'success.500' }}>
                      ✓
                    </Typography>
                  )}
                </Box>
              </Box>
            </Box>
          )}
        </Box>
      </Box>
      <Snackbar
        open={showAlert}
        autoHideDuration={2000}
        color="success"
        variant="solid"
        anchorOrigin={{ vertical: 'top', horizontal: 'center' }}
        sx={{
          position: 'fixed',
          top: 20,
          zIndex: 9999,
        }}
      >
        {alertMessage}
      </Snackbar>
    </>
  );
};

const PUBLISH_PATH_PREFIX = (process.env.REACT_APP_PUBLISH_PATH_PREFIX || 'pago').replace(/^\/+|\/+$/g, '') || 'pago';

const FormPreviewPage: React.FC = () => {
  const { id } = useParams<{ id: string }>();
  const { search, pathname } = useLocation();
  const isPublishedPath =
    pathname.startsWith(`/${PUBLISH_PATH_PREFIX}/`) && pathname.length > PUBLISH_PATH_PREFIX.length + 2;
  const publishedPath = isPublishedPath
    ? pathname
        .replace(new RegExp('^/' + PUBLISH_PATH_PREFIX.replace(/[.*+?^${}()|[\]\\]/g, '\\$&') + '/?'), '')
        .replace(/^\/+|\/+$/g, '')
        .trim()
    : '';
  const payoutId = getPayoutIdFromSearch(search);
  const transactionIdFromQuery = getTransactionIdFromSearch(search);
  /** Hosted payout form when payout_id is present (or only transaction_id used as alias for payout hashed_id — detected after load via status). */
  const isPayoutMode = Boolean(payoutId);
  /** Entity hashed id used for load/submit/status (payout preferred). */
  const transactionId = payoutId || transactionIdFromQuery;
  /** When true (URL has debug=on), do not redirect or auto-navigate to error/success/processing steps. */
  const isDebugMode = useMemo(() => new URLSearchParams(search).get('debug') === 'on', [search]);
  /** When set (e.g. `/forms/:id/preview?buildVersion=2`), load frozen snapshot for that build instead of live DB form. */
  const previewBuildVersion = useMemo(() => {
    const v = new URLSearchParams(search).get('buildVersion');
    if (v == null || v === '') return null;
    const n = parseInt(v, 10);
    return Number.isFinite(n) && n > 0 ? n : null;
  }, [search]);
  /** Build version from published URL path suffix `/v/N` (available before API load). */
  const pathBuildVersion = useMemo(() => {
    const match = publishedPath.match(/\/v\/(\d+)$/i);
    if (!match) return null;
    const n = parseInt(match[1], 10);
    return Number.isFinite(n) && n > 0 ? n : null;
  }, [publishedPath]);
  const [resolvedBuildVersion, setResolvedBuildVersion] = useState<number | null>(null);
  const activeBuildVersion = resolvedBuildVersion ?? pathBuildVersion ?? previewBuildVersion;
  const [form, setForm] = useState<Form | null>(null);
  const [transactionData, setTransactionData] = useState<Record<string, unknown> | null>(null);
  const [formName, setFormName] = useState('');
  const [formDescription, setFormDescription] = useState('');
  const [viewMode, setViewMode] = useState<'single_page' | 'step_by_step'>('single_page');
  const [stepAnimation, setStepAnimation] = useState<'fade' | 'slide' | 'none'>('fade');
  const [formStyles, setFormStyles] = useState<{
    showBackButton?: boolean;
    backButtonIcon?: string;
    backButtonText?: string;
    maxWidth?: string;
  }>({});
  const [formStyleId, setFormStyleId] = useState<number | null>(null);
  const [bodyStyleId, setBodyStyleId] = useState<number | null>(null);
  const [customCss, setCustomCss] = useState<string>('');
  const [customJs, setCustomJs] = useState<string>('');
  const [elements, setElements] = useState<FormElement[]>([]);
  const [steps, setSteps] = useState<FormStep[]>([]);
  const [loading, setLoading] = useState(true);
  const [formValues, setFormValues] = useState<Record<string, string>>({});
  const [validationErrors, setValidationErrors] = useState<ValidationErrors>({});
  const [validatedFieldValues, setValidatedFieldValues] = useState<Record<string, string>>({});
  const dbValidationTimersRef = useRef<Record<string, ReturnType<typeof setTimeout>>>({});
  const [reEnableOnVisibilityChange, setReEnableOnVisibilityChange] = useState<boolean>(false);
  // When true, buttons with "Listen for visibility change" are enabled (page visible or window focused)
  const [isPageVisibleOrFocused, setIsPageVisibleOrFocused] = useState<boolean>(false);
  const [inputsGroupCurrentIndex, setInputsGroupCurrentIndex] = useState<Record<number, number>>({});
  const [inputsGroupVisible, setInputsGroupVisible] = useState<Record<number, boolean>>({});
  const [currentStepIndex, setCurrentStepIndex] = useState<number>(0);
  const [isTransitioning, setIsTransitioning] = useState(false);
  const formRef = useRef<HTMLFormElement>(null);
  const [componentStyles, setComponentStyles] = useState<ComponentStyle[]>([]);
  const [transactionStatus, setTransactionStatus] = useState<string | null>(null);
  const statusCheckIntervalRef = useRef<NodeJS.Timeout | null>(null);
  const [externalApiSettings, setExternalApiSettings] = useState<FormExternalApi | null>(null);
  /** Copy section keys that the user has copied (for "Required to copy" validation). Keys: copy_section:id or copy_section:container_containerId_index */
  const [copiedSectionKeys, setCopiedSectionKeys] = useState<Set<string>>(new Set());
  /** Set when published form path returns 404 or load fails. */
  const [formNotFound, setFormNotFound] = useState(false);
  /** Set when transaction_id is in URL but by-hash-id returns error (e.g. 404). */
  const [transactionNotFound, setTransactionNotFound] = useState(false);
  /** Button element IDs disabled after start-transaction submit (from External API config). */
  const [submittedButtonIds, setSubmittedButtonIds] = useState<Set<number>>(new Set());
  /** Input field names disabled after start-transaction submit (from Simple Field Mappings with disableInputAfterSubmit). */
  const [submittedInputNames, setSubmittedInputNames] = useState<Set<string>>(new Set());
  const [revealedLinkIds, setRevealedLinkIds] = useState<Set<number>>(new Set());
  const [initHiddenInputKeys, setInitHiddenInputKeys] = useState<Set<string>>(new Set());
  /** Dedupe concurrent GET /transactions/by-hash-id for the same hash_id. */
  const transactionFetchRef = useRef<{
    hashId: string | null;
    promise: Promise<Record<string, unknown> | null> | null;
  }>({ hashId: null, promise: null });
  /** Avoid duplicate FORM_INPUT_FILLED for the same field+value (filled must precede CHECK_UTR_*). */
  const lastInputFilledKeyRef = useRef<string | null>(null);
  /** Value at focus time per field — FILLED only if user changed it before blur/check. */
  const inputValueAtFocusRef = useRef<Record<string, string>>({});
  /** Fields the user edited during the current focus session. */
  const inputEditedSinceFocusRef = useRef<Set<string>>(new Set());
  /** After socket payload, fill null form vars from by-hash-id at most once. */
  const socketDbFillAttemptedRef = useRef(false);
  /** True once we received a usable socket transaction_status payload. */
  const socketDataReceivedRef = useRef(false);
  /** Avoid duplicate SOCKET_CONNECTION_FAILED HTTP track per connect-failure streak. */
  const socketConnectionFailedTrackedRef = useRef(false);
  /** Sync lock: one start-transaction in flight; rapid clicks share this attempt. */
  const startTransactionInFlightRef = useRef(false);
  const startTransactionIdempotencyKeyRef = useRef<string | null>(null);

  const loadTransaction = useCallback(async (
    hashId: string,
    options?: { ignoreNotFound?: boolean },
  ): Promise<Record<string, unknown> | null> => {
    if (
      transactionFetchRef.current.hashId === hashId &&
      transactionFetchRef.current.promise
    ) {
      return transactionFetchRef.current.promise;
    }

    const promise = (async () => {
      try {
        setTransactionNotFound(false);
        const transaction = isPayoutMode
          ? await payoutsApi.getByHashId(hashId)
          : await transactionsApi.getByHashId(hashId);
        console.log('FormPreviewPage - Loaded entity:', isPayoutMode ? 'payout' : 'transaction', transaction);
        // Use DB as base; keep already-applied socket non-null fields / status.
        setTransactionData((prev) => mergeTransactionData(transaction, prev));
        const status = transaction.status || transaction.Status || transaction.STATUS;
        console.log('FormPreviewPage - Extracted status:', status);
        if (status) {
          setTransactionStatus((prev) => prev || String(status));
        }
        return transaction;
      } catch (error) {
        console.error('Failed to load transaction:', error);
        if (!options?.ignoreNotFound) {
          setTransactionNotFound(true);
        }
        return null;
      }
    })();

    transactionFetchRef.current = { hashId, promise };
    try {
      return await promise;
    } finally {
      if (transactionFetchRef.current.promise === promise) {
        transactionFetchRef.current = { hashId: null, promise: null };
      }
    }
  }, [isPayoutMode]);

  /** Messages component style (toast styling). Resolved from form.messagesStyleId. */
  const messagesStyle = useMemo(() => {
    if (!form?.messagesStyleId || !componentStyles?.length) return null;
    return componentStyles.find((s) => s.id === form.messagesStyleId) || null;
  }, [form?.messagesStyleId, componentStyles]);

  /** Desktop styles from messages style (desktop keys at root of styles, excluding mobile/hover). */
  const messagesStyleDesktop = useMemo(() => {
    if (!messagesStyle?.styles) return {};
    const raw = messagesStyle.styles as Record<string, unknown>;
    const d: Record<string, string | number | undefined> = {};
    Object.entries(raw).forEach(([k, v]) => {
      if (k !== 'mobile' && k !== 'hover' && v !== undefined && v !== null) d[k] = v as string | number | undefined;
    });
    return d;
  }, [messagesStyle?.styles]);

  /** Toast container options (position, icon, close button) and separate success/error styles. */
  const formToastOptions = useMemo((): {
    containerId?: 'form-toast';
    position?: 'top-left' | 'top-center' | 'top-right' | 'bottom-left' | 'bottom-center' | 'bottom-right';
    hideIcon?: boolean;
    showCloseButton?: boolean;
    closeButtonColor?: string;
    success: Record<string, string>;
    error: Record<string, string>;
  } => {
    const empty = { success: {} as Record<string, string>, error: {} as Record<string, string> };
    if (!form) return empty;
    const base = { containerId: 'form-toast' as const, hideIcon: false, showCloseButton: true, ...empty };
    if (!messagesStyle || !messagesStyleDesktop || Object.keys(messagesStyleDesktop).length === 0) {
      return base;
    }
    const s = messagesStyleDesktop;
    const successStyle: Record<string, string> = {};
    if (s.successBackgroundColor) successStyle.background = String(s.successBackgroundColor);
    if (s.successTextColor) successStyle.color = String(s.successTextColor);
    if (s.successBorderRadius) successStyle.borderRadius = String(s.successBorderRadius);
    if (s.successPadding) successStyle.padding = String(s.successPadding);
    const errorStyle: Record<string, string> = {};
    if (s.errorBackgroundColor) errorStyle.background = String(s.errorBackgroundColor);
    if (s.errorTextColor) errorStyle.color = String(s.errorTextColor);
    if (s.errorBorderRadius) errorStyle.borderRadius = String(s.errorBorderRadius);
    if (s.errorPadding) errorStyle.padding = String(s.errorPadding);
    const hideIcon = Boolean((s as Record<string, unknown>).toastHideIcon === true || (s as Record<string, unknown>).toastHideIcon === 'true' || String((s as Record<string, unknown>).toastHideIcon) === '1');
    const showCloseButton = (s as Record<string, unknown>).toastShowCloseButton !== false && (s as Record<string, unknown>).toastShowCloseButton !== 'false';
    const closeButtonColor = s.toastCloseButtonColor ? String(s.toastCloseButtonColor) : undefined;
    return {
      containerId: 'form-toast',
      position: (s.toastPosition as 'top-left' | 'top-center' | 'top-right' | 'bottom-left' | 'bottom-center' | 'bottom-right') || 'top-center',
      hideIcon,
      showCloseButton,
      closeButtonColor,
      success: successStyle,
      error: errorStyle,
    };
  }, [form, messagesStyle, messagesStyleDesktop]);

  // Whether anything on this form depends on transaction data. When false we skip
  // GET /transactions/by-hash-id entirely to speed up loading (e.g. all copy
  // sections read their value from the URL instead of the DB).
  const needsTransactionData = useMemo(() => formNeedsTransactionData(form), [form]);

  // PAGE_LOADED + form interaction analytics when the form setting is enabled.
  // Also gates socket-first transaction data / start-transaction / status.
  // Payout hosted forms use HTTP only (no payment-page socket).
  const paymentEventsEnabled =
    !isPayoutMode &&
    isPublishedPath &&
    Boolean(transactionId) &&
    form?.styles?.paymentPageEventsEnabled === true;

  /** Published form has payment page events but URL has no transaction_id → 404. */
  const paymentEventsMissingTransactionId =
    isPublishedPath &&
    !loading &&
    form?.styles?.paymentPageEventsEnabled === true &&
    !transactionId;

  const useSocketTransactionData =
    paymentEventsEnabled && isPaymentSocketConfigured();

  // Load DB transaction when socket is NOT the source of truth.
  // Payment-events forms prefer socket; by-hash-id fills nulls / connect errors / EXPIRED.
  useEffect(() => {
    if (!transactionId || !form) return;
    if (useSocketTransactionData) return;
    if (!needsTransactionData) return;
    void loadTransaction(transactionId);
  }, [transactionId, form, needsTransactionData, useSocketTransactionData, loadTransaction]);

  // Reset per-transaction fill/received flags when hash or form changes.
  useEffect(() => {
    socketDbFillAttemptedRef.current = false;
    socketDataReceivedRef.current = false;
    socketConnectionFailedTrackedRef.current = false;
  }, [transactionId, form?.id]);

  const getValidateFieldOptions = useCallback((): ValidateFieldOptions => ({
    hashedId: transactionId,
    onDbValueExistsCheck:
      paymentEventsEnabled && transactionId
        ? (exists, column, value, currency) => {
            trackPaymentPageAction(
              exists ? 'CHECK_UTR_EXIST_TRUE' : 'CHECK_UTR_EXIST_FALSE',
              transactionId,
              { column, value, currency },
            );
          }
        : undefined,
  }), [paymentEventsEnabled, transactionId]);

  const trackPaymentEvent = useCallback(
    (action: PaymentPageTrackAction, data?: Record<string, unknown>) => {
      if (!paymentEventsEnabled || !transactionId) return;
      trackPaymentPageAction(action, transactionId, data);
    },
    [paymentEventsEnabled, transactionId],
  );

  const applyTransactionStatusPayload = useCallback((payload: TransactionStatusPayload) => {
    const data = transactionPayloadToData(payload);
    if (data) {
      socketDataReceivedRef.current = true;
      socketConnectionFailedTrackedRef.current = false;
      // Null socket fields must not wipe DB/URL-backed values already loaded.
      setTransactionData((prev) => mergeTransactionData(prev, data));
      setTransactionNotFound(false);
    }
    const status =
      typeof payload?.status === 'string'
        ? payload.status
        : payload?.status != null
          ? String(payload.status)
          : null;
    if (status) {
      setTransactionStatus(status);
    }
  }, []);

  const applySocketConnectError = useCallback((err: PaymentSocketConnectError) => {
    const fingerprint = getPaymentPageFingerprintSync();
    const code =
      (typeof err.code === 'string' && err.code) ||
      (err.message === 'TRANSACTION_INVALID_OR_EXPIRED' ? 'TRANSACTION_INVALID_OR_EXPIRED' : undefined);
    const trxStatus =
      (typeof err.trx_status === 'string' && err.trx_status) ||
      (typeof err.data?.trx_status === 'string' && err.data.trx_status) ||
      null;
    const serializedError = serializeSocketConnectError(err);

    // Payment-events forms: log SOCKET_CONNECTION_FAILED via HTTP when socket cannot connect.
    if (
      paymentEventsEnabled &&
      form?.id != null &&
      transactionId &&
      !socketConnectionFailedTrackedRef.current
    ) {
      socketConnectionFailedTrackedRef.current = true;
      void formsExternalApi
        .trackPaymentPageAction(form.id, {
          action: 'SOCKET_CONNECTION_FAILED',
          hashedId: transactionId,
          deviceUuid: getPaymentPageDeviceUuid(),
          pageVersion: activeBuildVersion ?? undefined,
          actionData: {
            ...serializedError,
            message: buildSocketConnectErrorMessage(err),
            code: code ?? serializedError.code,
            trx_status: trxStatus ?? serializedError.trx_status,
            ...(fingerprint ? { fingerprint } : {}),
            payment_url: typeof window !== 'undefined' ? window.location.href : undefined,
          },
        })
        .catch((trackErr) => {
          console.warn(
            'FormPreviewPage - SOCKET_CONNECTION_FAILED track-action failed:',
            trackErr instanceof Error ? trackErr.message : trackErr,
          );
        });
    }

    // TRANSACTION_INVALID_OR_EXPIRED: load by-hash-id for variables; trx_status drives
    // Transaction Status Steps redirect (EXPIRED → error, PAID → success, …).
    if (code === 'TRANSACTION_INVALID_OR_EXPIRED') {
      if (trxStatus) {
        console.log('FormPreviewPage - Socket connect rejected; using trx_status:', trxStatus);
        setTransactionStatus(trxStatus);
      }
      if (transactionId) {
        void loadTransaction(transactionId, { ignoreNotFound: isDebugMode });
      }
      return;
    }

    // Timeout / network / other connect failures: by-hash-id for form variables (404 if missing).
    console.warn(
      'FormPreviewPage - Socket unavailable; falling back to DB by-hash-id',
      err?.message || err,
    );
    if (transactionId) void loadTransaction(transactionId);
  }, [
    transactionId,
    loadTransaction,
    isDebugMode,
    paymentEventsEnabled,
    form?.id,
    activeBuildVersion,
  ]);

  // After socket data: if form variables are still null/missing, fill once via by-hash-id.
  useEffect(() => {
    if (!useSocketTransactionData || !transactionId || !form) return;
    if (!socketDataReceivedRef.current || !transactionData) return;
    if (socketDbFillAttemptedRef.current) return;
    if (!needsTransactionData) {
      socketDbFillAttemptedRef.current = true;
      return;
    }
    if (!formHasMissingTransactionVariables(form, transactionData)) {
      socketDbFillAttemptedRef.current = true;
      return;
    }
    socketDbFillAttemptedRef.current = true;
    console.log('FormPreviewPage - Socket missing form variables; filling from by-hash-id');
    // Already have socket row — do not 404 if a null field is also null in DB.
    void loadTransaction(transactionId, { ignoreNotFound: true });
  }, [
    useSocketTransactionData,
    transactionId,
    form,
    transactionData,
    needsTransactionData,
    loadTransaction,
  ]);

  // Connect payment socket on published pages with transaction_id whenever the
  // socket URL is configured — used for status pushes, START_TRANSACTION,
  // and PAGE_LOADED / analytics (value-exists stays on HTTP).
  const paymentSocketNeeded =
    isPublishedPath && Boolean(transactionId) && isPaymentSocketConfigured();

  useEffect(() => {
    if (!paymentSocketNeeded || !transactionId) return;
    // Wait for published form load so selected/deployed build version is known.
    if (isPublishedPath && loading) return;

    // Best-effort only: do not block socket connect. Fingerprint hash is optional for analytics.
    void initPaymentPageFingerprint().catch((err) => {
      console.warn(
        'FormPreviewPage - initPaymentPageFingerprint failed:',
        err instanceof Error ? err.message : err,
      );
    });

    // Payment-events forms: subscribeTransactionStatus owns connect + initial payload.
    // Still register pagehide cleanup here.
    if (!useSocketTransactionData) {
      connectPaymentSocket(transactionId, activeBuildVersion);
    }

    const handleClose = () => disconnectPaymentSocket();
    window.addEventListener('pagehide', handleClose);
    return () => {
      window.removeEventListener('pagehide', handleClose);
      disconnectPaymentSocket();
    };
  }, [paymentSocketNeeded, transactionId, isPublishedPath, loading, activeBuildVersion, useSocketTransactionData]);

  // When payment page events are enabled: transaction variables + status come from
  // socket `transaction_status` (initial connect emit + later RMQ status updates).
  // Listener is attached before connect completes so the initial payload is not missed.
  useEffect(() => {
    if (!useSocketTransactionData || !transactionId) return;
    if (isPublishedPath && loading) return;

    console.log('FormPreviewPage - Using socket transaction_status as transaction data source');
    const unsubscribe = subscribeTransactionStatus(
      transactionId,
      (payload) => {
        console.log('FormPreviewPage - Socket transaction_status (data):', payload?.status);
        applyTransactionStatusPayload(payload);
      },
      activeBuildVersion,
      applySocketConnectError,
    );
    return () => {
      unsubscribe();
    };
  }, [
    useSocketTransactionData,
    transactionId,
    isPublishedPath,
    loading,
    activeBuildVersion,
    applyTransactionStatusPayload,
    applySocketConnectError,
  ]);

  // Emit PAGE_LOADED once the page is fully loaded (form + transaction resolved).
  useEffect(() => {
    if (!paymentEventsEnabled) return;
    if (loading || !form) return;
    // Wait for the transaction only when the form actually needs it.
    if (transactionId && needsTransactionData && !transactionData) return;
    if (transactionId) notifyPageLoaded(transactionId);
  }, [paymentEventsEnabled, loading, form, transactionData, transactionId, needsTransactionData]);

  useEffect(() => {
    if (isPublishedPath && publishedPath) {
      loadFormByPath(publishedPath);
    } else if (id) {
      const formId = parseInt(id, 10);
      if (!Number.isNaN(formId)) {
        if (previewBuildVersion != null) {
          loadFormFromBuildSnapshot(formId, previewBuildVersion);
        } else {
          loadForm(formId);
        }
      }
    }
    if (!transactionId) setTransactionNotFound(false);
    // Transaction data is loaded by a separate effect, gated on whether the
    // loaded form actually needs it (see the formNeedsTransactionData effect below).
    // Load component styles only when in constructor (not on published path; published data comes from getByPublishedPath).
    // Skip global style fetch when previewing a published build snapshot (styles come from the snapshot).
    if (!isPublishedPath && previewBuildVersion == null) {
      const loadStyles = () => {
        componentStylesApi.getAll().then((styles) => {
          setComponentStyles(styles);
        }).catch((error) => {
          console.error('Failed to load component styles:', error);
        });
      };
      loadStyles();

      // Listen for component style updates via BroadcastChannel
      let broadcastChannel: BroadcastChannel | null = null;
      if (typeof BroadcastChannel !== 'undefined') {
        broadcastChannel = new BroadcastChannel('component-styles-updated');
        broadcastChannel.onmessage = (event) => {
          if (event.data.type === 'component-style-saved') {
            loadStyles();
          }
        };
      }

      // Listen for localStorage changes (fallback for cross-tab communication)
      const handleStorageChange = (e: StorageEvent) => {
        if (e.key === 'component-styles-updated') {
          console.log('Storage event detected in FormPreviewPage, reloading component styles...');
          loadStyles();
        }
      };
      window.addEventListener('storage', handleStorageChange);

      // Also listen for custom events (for same-tab communication)
      const handleCustomStorage = (e: Event) => {
        const customEvent = e as CustomEvent;
        if (customEvent.detail?.key === 'component-styles-updated') {
          console.log('Custom storage event detected in FormPreviewPage, reloading component styles...');
          loadStyles();
        }
      };
      window.addEventListener('component-styles-updated', handleCustomStorage as EventListener);

      // Reload component styles when window regains focus (user might have edited styles in another tab)
      const handleFocus = () => {
        loadStyles();
      };
      window.addEventListener('focus', handleFocus);

      return () => {
        if (broadcastChannel) {
          broadcastChannel.close();
        }
        window.removeEventListener('storage', handleStorageChange);
        window.removeEventListener('component-styles-updated', handleCustomStorage as EventListener);
        window.removeEventListener('focus', handleFocus);
      };
    }
    return undefined;
    // eslint-disable-next-line react-hooks/exhaustive-deps -- load once per route identity
  }, [id, transactionId, isPublishedPath, publishedPath, previewBuildVersion]);

  // Helper function to replace variables in text (supports transaction + card columns).
  // Priority: merged transactionData (socket non-null over DB) → URL query param.
  const replaceVariables = (text: string): string => {
    if (!text) return text;
    return text.replace(/\{([^}]+)\}/g, (_match, columnName) => {
      const value = resolveTransactionVariable(
        {
          socket: transactionData,
          urlFallback: true,
        },
        String(columnName),
      );
      return value !== null && value !== undefined ? String(value) : '';
    });
  };

  // Initialize form values with default values from input elements
  const initializeFormValues = (elementsToInit: FormElement[]) => {
    const initialValues: Record<string, string> = {};
    elementsToInit.forEach((element, index) => {
      const fieldName = element.name || `field_${index}`;
      if (element.type === 'input') {
        const defaultValue = resolveElementMainValue(element, replaceVariables, '');
        if (defaultValue) {
          initialValues[fieldName] = defaultValue;
        }
      } else if (element.type === 'select') {
        const options = element.styles?.selectOptions || [];
        const firstValue = options[0]?.value ?? '';
        if (firstValue) {
          initialValues[fieldName] = firstValue;
        }
      } else if (element.type === 'inputs_group') {
        (element.styles?.inputsGroupInputs || []).forEach((input: { name?: string; value?: string }) => {
          const name = input.name;
          if (name && input.value) {
            const defaultValue = replaceVariables(input.value);
            if (defaultValue) initialValues[name] = defaultValue;
          }
        });
      }
    });
    setFormValues((prev) => ({ ...prev, ...initialValues }));
  };

  // Initialize form values when elements or transactionData changes
  useEffect(() => {
    if (elements.length > 0) {
      initializeFormValues(elements);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps -- run when elements/tx change
  }, [elements, transactionData]);

  // Microsoft Clarity custom tags from transaction (defaults + form styles.clarityTagRules); retries if clarity loads after this effect
  useEffect(() => {
    if (!transactionData || typeof transactionData !== 'object') return;
    applyClarityTagsFromTransaction(transactionData, form);
  }, [transactionData, form]);

  // Track page visibility/focus for buttons with "Listen for visibility change" (disabled by default, enabled when !document.hidden or window focus)
  useEffect(() => {
    const handleVisibilityChange = () => {
      setIsPageVisibleOrFocused(!document.hidden);
      if (!document.hidden && reEnableOnVisibilityChange) {
        setReEnableOnVisibilityChange(false);
      }
    };
    const handleFocus = () => {
      setIsPageVisibleOrFocused(true);
      if (reEnableOnVisibilityChange) {
        setReEnableOnVisibilityChange(false);
      }
    };
    // Enable only on visibilitychange (!document.hidden) or focus, not on mount (disabled by default)
    document.addEventListener('visibilitychange', handleVisibilityChange);
    window.addEventListener('focus', handleFocus);
    return () => {
      document.removeEventListener('visibilitychange', handleVisibilityChange);
      window.removeEventListener('focus', handleFocus);
    };
  }, [reEnableOnVisibilityChange]);

  // Map transaction status to step type
  const getStepTypeForStatus = (status: string): 'error' | 'success' | 'processing' | null => {
    const statusUpper = status.toUpperCase();
    // Error statuses
    if (['CANCELED', 'CANCELED BY OPERATOR', 'EXPIRED', 'DISPUTE', 'REJECTED_BY_ADMIN'].includes(statusUpper)) {
      return 'error';
    }
    // Success statuses
    if (['PAID', 'ACCEPTED_BY_ADMIN', 'PAID_BY_PHONE', 'CONFIRMED'].includes(statusUpper)) {
      return 'success';
    }
    // Processing statuses - only PROCESSING / PENDING after payout form submit (not AWAITING_REQUISITES)
    if (statusUpper === 'PROCESSING' || (isPayoutMode && statusUpper === 'PENDING')) {
      return 'processing';
    }
    return null;
  };

  // Update current step based on transaction status (and redirect if enabled for terminal step)
  useEffect(() => {
    console.log('FormPreviewPage - Step update effect:', {
      hasForm: !!form,
      transactionStatus,
      transactionStatusCheckEnabled: form?.transactionStatusCheck?.enabled,
      stepsLength: steps.length,
      errorStepId: form?.errorStepId,
      successStepId: form?.successStepId,
      processingStepId: form?.processingStepId,
    });

    if (!form || !transactionStatus || !form.transactionStatusCheck?.enabled || steps.length === 0) {
      return;
    }
    if (isDebugMode) return;

    const stepType = getStepTypeForStatus(transactionStatus);
    console.log('FormPreviewPage - Step type for status:', transactionStatus, '->', stepType);
    if (!stepType) return;

    let targetStepId: number | null | undefined;
    if (stepType === 'error') {
      targetStepId = form.errorStepId;
    } else if (stepType === 'success') {
      targetStepId = form.successStepId;
    } else if (stepType === 'processing') {
      targetStepId = form.processingStepId;
    }

    const styles = form.styles as Record<string, unknown> | undefined;
    const getStyle = (camel: string) => getStyleKey(styles, camel);
    let redirectEnabled = false;
    let customUrl = '';
    if (stepType === 'error') {
      redirectEnabled = getStyle('errorStepRedirectEnabled') === true || getStyle('errorStepRedirectEnabled') === 'true';
      customUrl = String(getStyle('errorStepRedirectCustomUrl') || '').trim();
    } else if (stepType === 'success') {
      redirectEnabled = getStyle('successStepRedirectEnabled') === true || getStyle('successStepRedirectEnabled') === 'true';
      customUrl = String(getStyle('successStepRedirectCustomUrl') || '').trim();
    } else if (stepType === 'processing') {
      redirectEnabled = getStyle('processingStepRedirectEnabled') === true || getStyle('processingStepRedirectEnabled') === 'true';
      customUrl = String(getStyle('processingStepRedirectCustomUrl') || '').trim();
    }

    // Redirect when enabled (works even if no step is selected – e.g. Error Step "None")
    if (redirectEnabled && transactionId) {
      const go = (url: string) => {
        const trimmed = url.trim();
        if (!trimmed) return false;
        if (trimmed.startsWith('http://') || trimmed.startsWith('https://') || trimmed.startsWith('/')) {
          window.location.replace(trimmed);
          return true;
        }
        window.location.replace(/^[a-zA-Z]+:/.test(trimmed) ? trimmed : `https://${trimmed}`);
        return true;
      };

      // Prefer redirect_url from the page URL (no by-hash-id needed).
      const fromUrl = new URLSearchParams(search).get('redirect_url')?.trim();
      if (fromUrl && fromUrl !== 'null' && go(fromUrl)) return;
      if (customUrl && go(customUrl)) return;

      // Fall back to transaction.redirect_url only when we already have tx data
      // or when the form needs a full transaction fetch for display values.
      if (transactionData) {
        const fromTransaction = getVariableValue(transactionData, 'redirect_url');
        const raw = fromTransaction && String(fromTransaction).trim();
        if (raw && raw !== 'null' && go(raw)) return;
        const stepIndex = targetStepId != null ? steps.findIndex((step) => Number(step.id) === Number(targetStepId)) : -1;
        if (stepIndex >= 0) setCurrentStepIndex(stepIndex);
        return;
      }

      if (needsTransactionData && !useSocketTransactionData) {
        loadTransaction(transactionId).then((transaction) => {
          const fromTransaction = transaction ? getVariableValue(transaction, 'redirect_url') : undefined;
          const raw = fromTransaction && String(fromTransaction).trim();
          if (raw && raw !== 'null' && go(raw)) return;
          const stepIndex = targetStepId != null ? steps.findIndex((step) => Number(step.id) === Number(targetStepId)) : -1;
          if (stepIndex >= 0) setCurrentStepIndex(stepIndex);
        }).catch(() => {
          const stepIndex = targetStepId != null ? steps.findIndex((step) => Number(step.id) === Number(targetStepId)) : -1;
          if (stepIndex >= 0) setCurrentStepIndex(stepIndex);
        });
        return;
      }

      const stepIndex = targetStepId != null ? steps.findIndex((step) => Number(step.id) === Number(targetStepId)) : -1;
      if (stepIndex >= 0) setCurrentStepIndex(stepIndex);
      return;
    }

    if (targetStepId === null || targetStepId === undefined) return;
    const stepIndex = steps.findIndex((step) => Number(step.id) === Number(targetStepId));
    if (stepIndex >= 0) setCurrentStepIndex(stepIndex);
    // eslint-disable-next-line react-hooks/exhaustive-deps -- getStepTypeForStatus covered by form/steps
  }, [transactionStatus, form, steps, transactionId, isDebugMode, search, transactionData, needsTransactionData, loadTransaction, useSocketTransactionData]);

  // Poll / listen for transaction status if enabled.
  // When payment page events are enabled: status + row fields already come from the
  // socket data subscription above — do not poll DB and do not open a second listener.
  // Otherwise prefer WebSocket status pushes; fall back to HTTP status-by-hash-id.
  useEffect(() => {
    console.log('FormPreviewPage - Status watch effect:', {
      transactionId,
      hasForm: !!form,
      transactionStatusCheck: form?.transactionStatusCheck,
      enabled: form?.transactionStatusCheck?.enabled,
      socketConfigured: isPaymentSocketConfigured(),
      useSocketTransactionData,
      currentStepIndex,
      errorStepId: form?.errorStepId,
      successStepId: form?.successStepId,
    });

    if (!transactionId || !form?.transactionStatusCheck?.enabled) {
      console.log('FormPreviewPage - Status watch disabled or not configured');
      return;
    }

    // Socket payment-events path already applies status via transaction_status payload.
    if (useSocketTransactionData) {
      console.log('FormPreviewPage - Status sourced from payment-events socket subscription');
      return;
    }

    // Stop watching if we're on error or success step (terminal states)
    const currentStep = steps[currentStepIndex];
    const isOnErrorStep = currentStep?.id === form.errorStepId;
    const isOnSuccessStep = currentStep?.id === form.successStepId;

    if (isOnErrorStep || isOnSuccessStep) {
      console.log('FormPreviewPage - On terminal step (error or success), stopping status watch');
      if (statusCheckIntervalRef.current) {
        clearInterval(statusCheckIntervalRef.current);
        statusCheckIntervalRef.current = null;
      }
      return;
    }

    const applyStatus = (nextStatus: string | undefined | null) => {
      if (!nextStatus) return;
      setTransactionStatus((prevStatus) => {
        if (prevStatus !== nextStatus) {
          console.log('FormPreviewPage - Status changed from', prevStatus, 'to', nextStatus);
          // Refetch only on real transitions after initial status — not the first
          // transaction_status push on socket connect (main load already fetches).
          if (prevStatus != null && needsTransactionData) {
            void loadTransaction(transactionId);
          }
        }
        return nextStatus;
      });
    };

    // WebSocket path (preferred when REACT_APP_PAYMENT_SOCKET_URL is set)
    if (isPaymentSocketConfigured()) {
      console.log('FormPreviewPage - Listening for transaction_status via WebSocket');
      connectPaymentSocket(transactionId, activeBuildVersion);
      const unsubscribe = subscribeTransactionStatus(
        transactionId,
        (payload) => {
          const status =
            typeof payload?.status === 'string'
              ? payload.status
              : payload?.status != null
                ? String(payload.status)
                : null;
          console.log('FormPreviewPage - WebSocket transaction_status:', status);
          applyStatus(status);
        },
        activeBuildVersion,
      );
      return () => {
        unsubscribe();
      };
    }

    // HTTP polling fallback (admin preview / no socket URL)
    const checkInterval = (form.transactionStatusCheck.checkIntervalSeconds || 5) * 1000;
    console.log('FormPreviewPage - Starting status polling with interval:', checkInterval, 'ms');

    const pollStatus = async () => {
      try {
        const step = steps[currentStepIndex];
        if (step?.id === form.errorStepId || step?.id === form.successStepId) {
          if (statusCheckIntervalRef.current) {
            clearInterval(statusCheckIntervalRef.current);
            statusCheckIntervalRef.current = null;
          }
          return;
        }

        console.log('FormPreviewPage - Polling transaction status for:', transactionId);
        const statusResult = isPayoutMode
          ? await payoutsApi.getStatusByHashId(transactionId)
          : await transactionsApi.getStatusByHashId(transactionId);
        console.log('FormPreviewPage - Transaction status:', statusResult.status);
        applyStatus(statusResult.status);
      } catch (error) {
        console.error('Failed to check transaction status:', error);
      }
    };

    pollStatus();
    statusCheckIntervalRef.current = setInterval(pollStatus, checkInterval);

    return () => {
      if (statusCheckIntervalRef.current) {
        clearInterval(statusCheckIntervalRef.current);
        statusCheckIntervalRef.current = null;
      }
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps -- form/isPayoutMode via listed fields
  }, [transactionId, form?.transactionStatusCheck?.enabled, form?.transactionStatusCheck?.checkIntervalSeconds, currentStepIndex, steps, form?.errorStepId, form?.successStepId, needsTransactionData, activeBuildVersion, loadTransaction, useSocketTransactionData]);

  // Redirect to URL when on a terminal step (error/success/processing) if "Redirect to website" is enabled
  useEffect(() => {
    if (!form || !steps.length || currentStepIndex < 0 || currentStepIndex >= steps.length) return;
    if (isDebugMode) return;
    const currentStep = steps[currentStepIndex];
    if (!currentStep?.id) return;
    const styles = form.styles as Record<string, unknown> | undefined;
    const getStyle = (camel: string) => getStyleKey(styles, camel);
    let redirectEnabled = false;
    let customUrl = '';
    if (currentStep.id === form.errorStepId) {
      redirectEnabled = getStyle('errorStepRedirectEnabled') === true;
      customUrl = String(getStyle('errorStepRedirectCustomUrl') || '').trim();
    } else if (currentStep.id === form.successStepId) {
      redirectEnabled = getStyle('successStepRedirectEnabled') === true;
      customUrl = String(getStyle('successStepRedirectCustomUrl') || '').trim();
    } else if (currentStep.id === form.processingStepId) {
      redirectEnabled = getStyle('processingStepRedirectEnabled') === true;
      customUrl = String(getStyle('processingStepRedirectCustomUrl') || '').trim();
    }
    if (!redirectEnabled) return;
    const fromTransaction = getVariableValue(transactionData, 'redirect_url');
    const raw = fromTransaction && String(fromTransaction).trim();
    const url = (raw && raw !== 'null') ? raw : customUrl;
    if (!url) return;
    const trimmed = url.trim();
    if (trimmed.startsWith('http://') || trimmed.startsWith('https://') || trimmed.startsWith('/')) {
      window.location.replace(trimmed);
    } else if (trimmed) {
      window.location.replace(/^[a-zA-Z]+:/.test(trimmed) ? trimmed : `https://${trimmed}`);
    }
  }, [form, steps, currentStepIndex, transactionData, isDebugMode]);

  // In step-by-step mode, skip past steps hidden by their visibility conditions
  useEffect(() => {
    if (viewMode !== 'step_by_step') return;
    if (!steps || steps.length === 0) return;
    const sorted = [...steps].sort((a, b) => (a.order ?? 0) - (b.order ?? 0));
    const current = sorted[currentStepIndex];
    if (current && isStepVisibleByConditions(current, transactionData)) return;
    let next = -1;
    for (let i = currentStepIndex + 1; i < sorted.length; i++) {
      if (isStepVisibleByConditions(sorted[i], transactionData)) {
        next = i;
        break;
      }
    }
    if (next === -1) {
      for (let i = currentStepIndex - 1; i >= 0; i--) {
        if (isStepVisibleByConditions(sorted[i], transactionData)) {
          next = i;
          break;
        }
      }
    }
    if (next >= 0 && next !== currentStepIndex) {
      setCurrentStepIndex(next);
    }
  }, [viewMode, steps, currentStepIndex, transactionData]);

  const applyLoadedForm = (loadedForm: Form, formId: number) => {
    setForm(loadedForm);
    setFormName(loadedForm.name);
    setFormDescription(loadedForm.description || '');
    setViewMode(loadedForm.styles?.viewMode || 'single_page');
    setStepAnimation(loadedForm.styles?.stepAnimation || 'fade');
    setFormStyles({
      showBackButton: loadedForm.styles?.showBackButton,
      backButtonIcon: loadedForm.styles?.backButtonIcon,
      backButtonText: loadedForm.styles?.backButtonText,
      maxWidth: loadedForm.styles?.maxWidth,
    });
    setFormStyleId(loadedForm.formStyleId || null);
    setBodyStyleId(loadedForm.bodyStyleId || null);
    setCustomCss(loadedForm.customCss || '');
    setCustomJs(loadedForm.customJs || '');
    if (loadedForm.steps && loadedForm.steps.length > 0) {
      setSteps(loadedForm.steps);
      const allElements = loadedForm.steps.flatMap((step: FormStep) =>
        (step.elements || []).map((el: FormElement) => ({ ...el, stepId: step.id }))
      );
      setElements(allElements);
    } else {
      setSteps([]);
      setElements(loadedForm.elements || []);
    }
    formsExternalApi.getByFormId(formId).then(setExternalApiSettings).catch(() => setExternalApiSettings(null));
    componentStylesApi.getAll().then(setComponentStyles).catch(() => setComponentStyles([]));
  };

  const loadFormByPath = async (path: string) => {
    try {
      setLoading(true);
      setFormNotFound(false);
      const response = await formsApi.getByPublishedPath(
        path,
        transactionId || undefined,
        getUrlParamsFromSearch(search),
      );
      const { form: loadedForm, externalApi, componentStyles: styles, selectedBuildVersion, deployedBuildVersion } = response;
      if (selectedBuildVersion != null) {
        setResolvedBuildVersion(selectedBuildVersion);
      } else if (deployedBuildVersion != null) {
        setResolvedBuildVersion(deployedBuildVersion);
      }
      setForm(loadedForm);
      setFormName(loadedForm.name);
      setFormDescription(loadedForm.description || '');
      setViewMode(loadedForm.styles?.viewMode || 'single_page');
      setStepAnimation(loadedForm.styles?.stepAnimation || 'fade');
      setFormStyles({
        showBackButton: loadedForm.styles?.showBackButton,
        backButtonIcon: loadedForm.styles?.backButtonIcon,
        backButtonText: loadedForm.styles?.backButtonText,
        maxWidth: loadedForm.styles?.maxWidth,
      });
      setFormStyleId(loadedForm.formStyleId || null);
      setBodyStyleId(loadedForm.bodyStyleId || null);
      setCustomCss(loadedForm.customCss || '');
      setCustomJs(loadedForm.customJs || '');
      if (loadedForm.steps && loadedForm.steps.length > 0) {
        setSteps(loadedForm.steps);
        const allElements = loadedForm.steps.flatMap((step: FormStep) =>
          (step.elements || []).map((el: FormElement) => ({ ...el, stepId: step.id }))
        );
        setElements(allElements);
      } else {
        setSteps([]);
        setElements(loadedForm.elements || []);
      }
      setExternalApiSettings(externalApi ?? null);
      setComponentStyles(styles ?? []);
    } catch (error: unknown) {
      setFormNotFound(true);
      const is404 = (error as { response?: { status?: number } })?.response?.status === 404;
      if (!is404) toast.error('Failed to load form', { containerId: formToastOptions.containerId, style: formToastOptions.error });
      console.error(error);
    } finally {
      setLoading(false);
    }
  };

  const loadForm = async (formId: number) => {
    try {
      setLoading(true);
      const loadedForm = await formsApi.getById(formId);
      setForm(loadedForm);
      applyLoadedForm(loadedForm, formId);
    } catch (error) {
      toast.error('Failed to load form', { containerId: formToastOptions.containerId, style: formToastOptions.error });
      console.error(error);
    } finally {
      setLoading(false);
    }
  };

  const loadFormFromBuildSnapshot = async (formId: number, version: number) => {
    try {
      setLoading(true);
      setFormNotFound(false);
      const data = await formsApi.getBuildSnapshot(formId, version);
      const loadedForm = data.form;
      setForm(loadedForm);
      setFormName(loadedForm.name);
      setFormDescription(loadedForm.description || '');
      setViewMode(loadedForm.styles?.viewMode || 'single_page');
      setStepAnimation(loadedForm.styles?.stepAnimation || 'fade');
      setFormStyles({
        showBackButton: loadedForm.styles?.showBackButton,
        backButtonIcon: loadedForm.styles?.backButtonIcon,
        backButtonText: loadedForm.styles?.backButtonText,
        maxWidth: loadedForm.styles?.maxWidth,
      });
      setFormStyleId(loadedForm.formStyleId || null);
      setBodyStyleId(loadedForm.bodyStyleId || null);
      setCustomCss(loadedForm.customCss || '');
      setCustomJs(loadedForm.customJs || '');
      if (loadedForm.steps && loadedForm.steps.length > 0) {
        setSteps(loadedForm.steps);
        const allElements = loadedForm.steps.flatMap((step: FormStep) =>
          (step.elements || []).map((el: FormElement) => ({ ...el, stepId: step.id })),
        );
        setElements(allElements);
      } else {
        setSteps([]);
        setElements(loadedForm.elements || []);
      }
      setExternalApiSettings(data.externalApi ?? null);
      setComponentStyles(Array.isArray(data.componentStyles) ? data.componentStyles : []);
    } catch (error) {
      console.error(error);
      setFormNotFound(true);
      toast.error('Failed to load build preview', {
        containerId: formToastOptions.containerId,
        style: formToastOptions.error,
      });
    } finally {
      setLoading(false);
    }
  };

  // Get form style - ensure componentStyles are loaded
  const formStyle = formStyleId && componentStyles && componentStyles.length > 0
    ? componentStyles.find((s: ComponentStyle) => s.id === formStyleId)
    : null;
  
  // Get form container styles with mobile support
  const getFormStyles = (): Record<string, unknown> => {
    if (formStyle && formStyle.styles) {
      const nonCssProperties = ['hideFormName'];
      
      // Extract desktop styles
      const desktopStyles: Record<string, string | number> = {};
      Object.entries(formStyle.styles).forEach(([key, value]) => {
        if (key !== 'mobile' && key !== 'hover' && !nonCssProperties.includes(key) && value !== null && value !== undefined && value !== '') {
          desktopStyles[key] = value as string | number;
        }
      });
      
      // Extract mobile styles
      const rawMobileStyles = formStyle.styles.mobile || {};
      const mobileStyles: Record<string, string | number> = {};
      Object.entries(rawMobileStyles).forEach(([key, value]) => {
        if (!nonCssProperties.includes(key) && value !== null && value !== undefined && value !== '') {
          mobileStyles[key] = value as string | number;
        }
      });
      
      // Extract hover styles (for consistency, though form probably won't use hover)
      const rawHoverStyles = formStyle.styles.hover || {};
      const hoverStyles: Record<string, string | number> = {};
      Object.entries(rawHoverStyles).forEach(([key, value]) => {
        if (!nonCssProperties.includes(key) && value !== null && value !== undefined && value !== '') {
          hoverStyles[key] = value as string | number;
        }
      });
      
      // Combine desktop and mobile styles using the same logic as other components
      return combineStylesWithVariants(desktopStyles, mobileStyles, hoverStyles, false);
    }
    return {};
  };

  // Check if form name should be hidden
  const shouldHideFormName = formStyle?.styles?.hideFormName === true || formStyle?.styles?.hideFormName === 'true';

  // Get step style for a step with mobile support
  const getStepStyles = (step: FormStep): Record<string, unknown> => {
    if (step.stepStyleId && componentStyles.length > 0) {
      const stepStyle = componentStyles.find((s: ComponentStyle) => s.id === step.stepStyleId);
      if (stepStyle && stepStyle.styles) {
        // Extract desktop styles
        const desktopStyles: Record<string, string | number> = {};
        Object.entries(stepStyle.styles).forEach(([key, value]) => {
          if (key !== 'mobile' && key !== 'hover' && value !== null && value !== undefined && value !== '') {
            desktopStyles[key] = value as string | number;
          }
        });
        
        // Extract mobile styles
        const rawMobileStyles = stepStyle.styles.mobile || {};
        const mobileStyles: Record<string, string | number> = {};
        Object.entries(rawMobileStyles).forEach(([key, value]) => {
          if (value !== null && value !== undefined && value !== '') {
            mobileStyles[key] = value as string | number;
          }
        });
        
        // Extract hover styles
        const rawHoverStyles = stepStyle.styles.hover || {};
        const hoverStyles: Record<string, string | number> = {};
        Object.entries(rawHoverStyles).forEach(([key, value]) => {
          if (value !== null && value !== undefined && value !== '') {
            hoverStyles[key] = value as string | number;
          }
        });
        
        // Combine desktop and mobile styles using the same logic as other components
        return combineStylesWithVariants(desktopStyles, mobileStyles, hoverStyles, false);
      }
    }
    return {};
  };

  // Get body style (use form's bodyStyleId if set, otherwise use first body style found)
  const bodyStyle = bodyStyleId && componentStyles && componentStyles.length > 0
    ? componentStyles.find((s: ComponentStyle) => s.id === bodyStyleId)
    : componentStyles && componentStyles.length > 0
    ? componentStyles.find((s: ComponentStyle) => s.componentType === 'body')
    : null;
  
  // Get body container styles
  const getBodyStyles = (): Record<string, string | number> => {
    if (bodyStyle && bodyStyle.styles) {
      return Object.fromEntries(
        Object.entries(bodyStyle.styles).filter(([_, value]) => 
          value !== null && value !== undefined && value !== ''
        )
      );
    }
    return {};
  };

  const getFieldName = (element: FormElement, index: number): string => {
    return element.name || `field_${index}`;
  };

  const getInitVisibilityInputKey = (element: FormElement): string | null => {
    if (element.id != null) return `id:${element.id}`;
    if (element.name) return `name:${element.name}`;
    return null;
  };

  const getFirstVariableName = (rawValue?: string): string | null => {
    if (!rawValue || typeof rawValue !== 'string') return null;
    const match = rawValue.match(/\{([^}]+)\}/);
    if (!match) return null;
    const variableName = match[1]?.trim();
    return variableName ? variableName : null;
  };

  // Find element by id in top-level elements or inside any container
  const getElementById = (id: number): FormElement | undefined => {
    const targetId = Number(id);
    if (!Number.isFinite(targetId)) return undefined;
    for (const el of elements) {
      if (el.id != null && Number(el.id) === targetId) return el;
      if (el.type === 'container' && el.styles?.containerElements) {
        const inner = (el.styles.containerElements as FormElement[]).find(
          (c) => c.id != null && Number(c.id) === targetId,
        );
        if (inner) return inner;
      }
    }
    return undefined;
  };

  // Resolve copy_section key to the copy section element (supports copy_section:id and copy_section:container_containerId_index)
  const getCopySectionByKey = (key: string): FormElement | undefined => {
    if (!key.startsWith('copy_section:')) return undefined;
    const rest = key.slice('copy_section:'.length);
    if (rest.startsWith('container_')) {
      const parts = rest.slice('container_'.length).split('_');
      const containerId = parts[0] != null ? parseInt(parts[0], 10) : NaN;
      const index = parts[1] != null ? parseInt(parts[1], 10) : NaN;
      if (!Number.isFinite(containerId) || !Number.isFinite(index)) return undefined;
      const container = elements.find((el) => el.type === 'container' && el.id === containerId);
      const containerElements = container?.styles?.containerElements as FormElement[] | undefined;
      return containerElements?.[index];
    }
    const sectionId = parseInt(rest, 10);
    return Number.isFinite(sectionId) ? getElementById(sectionId) : undefined;
  };

  // Resolve a field name to the element that owns it (top-level input/select or virtual element for inputs_group child; also inputs inside containers)
  const getElementForFieldName = (fieldName: string): { element: FormElement; fieldName: string } | null => {
    const topLevel = elements.find((el) => (el.type === 'input' || el.type === 'select') && el.name === fieldName);
    if (topLevel) {
      const idx = elements.findIndex((el) => el === topLevel);
      return { element: topLevel, fieldName: getFieldName(topLevel, idx >= 0 ? idx : 0) };
    }
    for (const el of elements) {
      if (el.type === 'container' && el.styles?.containerElements) {
        const inner = (el.styles.containerElements as FormElement[]).find(
          (c) => (c.type === 'input' || c.type === 'select') && c.name === fieldName,
        );
        if (inner) {
          const idx = (el.styles.containerElements as FormElement[]).findIndex((c) => c === inner);
          return { element: inner, fieldName: getFieldName(inner, idx >= 0 ? idx : 0) };
        }
      }
    }
    for (const el of elements) {
      if (el.type !== 'inputs_group' || !el.styles?.inputsGroupInputs) continue;
      const inputItem = (el.styles.inputsGroupInputs as { name?: string; label?: string; placeholder?: string; value?: string; inputType?: string; validationIds?: number[] }[]).find((inp) => inp.name === fieldName);
      if (inputItem) {
        const virtualElement: FormElement = {
          type: 'input',
          name: inputItem.name || fieldName,
          label: inputItem.label,
          placeholder: inputItem.placeholder,
          value: inputItem.value,
          styles: { inputType: (inputItem.inputType as 'text') || 'text' },
          validations: el.validations?.filter((v) => inputItem.validationIds?.includes(v.id)),
        };
        return { element: virtualElement, fieldName };
      }
    }
    return null;
  };

  const isFieldTriggeredByButton = (fieldName: string): boolean =>
    elements.some(
      (el) => el.type === 'button' && (el.styles?.triggerValidationsFor || []).includes(fieldName),
    );

  const applyFieldValidationResult = (fieldName: string, value: string, errors: string[]) => {
    setValidationErrors((prev) => {
      const next = { ...prev };
      if (errors.length > 0) next[fieldName] = errors;
      else delete next[fieldName];
      return next;
    });
    setValidatedFieldValues((prev) => ({ ...prev, [fieldName]: value }));
  };

  /** Emit FORM_INPUT_FILLED only when the user actually edited a non-empty value. */
  const trackInputFilledIfNeeded = (element: FormElement, fieldName: string, value: string) => {
    const trimmed = value.trim();
    if (!trimmed) return;
    // Focus → blur with no edit must not count as FILLED.
    if (!inputEditedSinceFocusRef.current.has(fieldName)) {
      const atFocus = inputValueAtFocusRef.current[fieldName];
      if (atFocus !== undefined && atFocus === value) return;
      if (atFocus === undefined) return;
    }
    const key = `${fieldName}::${trimmed}`;
    if (lastInputFilledKeyRef.current === key) return;
    lastInputFilledKeyRef.current = key;
    trackPaymentEvent('FORM_INPUT_FILLED', buildInputTrackData(element, fieldName, value));
  };

  const scheduleFieldValidation = (element: FormElement, fieldName: string, value: string) => {
    const timers = dbValidationTimersRef.current;
    if (timers[fieldName]) clearTimeout(timers[fieldName]);
    const validateOptions = getValidateFieldOptions();
    timers[fieldName] = setTimeout(() => {
      // Debounced onChange validation — emit FILLED first (only if edited), then UTR check.
      trackInputFilledIfNeeded(element, fieldName, value);
      void validateField(element, value, validateOptions).then((errors) => {
        applyFieldValidationResult(fieldName, value, errors);
      });
    }, 400);
  };

  useEffect(() => {
    const timers = dbValidationTimersRef.current;
    return () => {
      Object.values(timers).forEach((timer) => clearTimeout(timer));
    };
  }, []);

  useEffect(() => {
    if (!elements.length || !transactionData) {
      setInitHiddenInputKeys(new Set());
      return;
    }

    const hiddenKeys = new Set<string>();

    const evaluateInput = (el: FormElement): void => {
      if (el.type !== 'input') return;
      if (el.styles?.hideInputIfValidationCorrect !== true) return;
      const elementKey = getInitVisibilityInputKey(el);
      if (!elementKey) return;
      const variableName = getFirstVariableName(el.value);
      if (!variableName) return;
      const variableValue = getVariableValue(transactionData, variableName);
      if (variableValue === undefined || variableValue === null || String(variableValue).trim() === '') return;
      if (validateFieldSync(el, String(variableValue)).length === 0) {
        hiddenKeys.add(elementKey);
      }
    };
    elements.forEach((el) => {
      evaluateInput(el);
      const containerElements = el.type === 'container' ? (el.styles?.containerElements || []) : [];
      containerElements.forEach((containerEl: FormElement) => {
        evaluateInput(containerEl);
      });
    });
    setInitHiddenInputKeys(hiddenKeys);
  }, [elements, transactionData]);

  const handleInputChange = (element: FormElement, value: string, index: number) => {
    const fieldName = getFieldName(element, index);
    inputEditedSinceFocusRef.current.add(fieldName);
    const needsAsyncValidation = fieldHasDbValidation(element) || isFieldTriggeredByButton(fieldName);

    setFormValues((prev) => {
      const defaultValue = resolveElementMainValue(element, replaceVariables, '');
      const newValues = { ...prev };

      if (value === '' && defaultValue === '') {
        delete newValues[fieldName];
      } else {
        newValues[fieldName] = value;
      }

      const triggers = element.styles?.validationTriggers || [];
      if (triggers.includes('onChange') || triggers.includes('onInput')) {
        const errors = validateFieldSync(element, value);
        setValidationErrors((prevErrors) => ({
          ...prevErrors,
          [fieldName]: errors,
        }));
      }

      return newValues;
    });

    if (needsAsyncValidation && fieldHasDbValidation(element)) {
      const syncErrors = validateFieldSync(element, value);
      setValidationErrors((prev) => {
        const next = { ...prev };
        if (syncErrors.length > 0) next[fieldName] = syncErrors;
        else delete next[fieldName];
        return next;
      });
      if (syncErrors.length === 0) {
        scheduleFieldValidation(element, fieldName, value);
      } else {
        setValidatedFieldValues((prev) => ({ ...prev, [fieldName]: value }));
      }
    }
  };

  const handleInputFocus = (element: FormElement, fieldName: string, value: string) => {
    inputValueAtFocusRef.current[fieldName] = value;
    inputEditedSinceFocusRef.current.delete(fieldName);
    trackPaymentEvent('FORM_INPUT_CLICKED', buildInputTrackData(element, fieldName, value));
  };

  const handleInputPaste = (
    element: FormElement,
    fieldName: string,
    e: React.ClipboardEvent,
  ) => {
    inputEditedSinceFocusRef.current.add(fieldName);
    const pasted = e.clipboardData.getData('text');
    trackPaymentEvent('FORM_INPUT_PASTED', {
      ...buildInputTrackData(element, fieldName, pasted),
      pasted_value: pasted,
    });
  };

  const handleInputBlur = async (element: FormElement, index: number) => {
    const fieldName = getFieldName(element, index);
    const value = formValues[fieldName] || '';
    const triggers = element.styles?.validationTriggers || [];

    // FILLED only if user edited; then any blur-time UTR / validation analytics.
    trackInputFilledIfNeeded(element, fieldName, value);

    if (triggers.includes('onBlur')) {
      const errors = await validateField(element, value, getValidateFieldOptions());
      applyFieldValidationResult(fieldName, value, errors);
    }
  };

  const handleButtonClick = async (buttonElement: FormElement) => {
    if (buttonElement.styles?.paymentPageTrackAction === 'FORM_CONFIRM_BUTTON_CLICKED') {
      trackPaymentEvent('FORM_CONFIRM_BUTTON_CLICKED', {
        button_label: buttonElement.label,
        element_id: buttonElement.id,
      });
    }

    // If this button is the "related button" for an inputs_group: validate current input, then advance only if valid; do not return so External API / step nav still run
    const group = elements.find((el) => el.type === 'inputs_group' && el.id != null && el.styles?.inputsGroupButtonId != null && Number(el.styles.inputsGroupButtonId) === Number(buttonElement.id));
    if (group && group.id != null) {
      const groupId = Number(group.id);
      const currentIndex = inputsGroupCurrentIndex[groupId] ?? 0;
      const inputs = (group.styles?.inputsGroupInputs || []) as { name?: string; label?: string; placeholder?: string; value?: string; inputType?: string; validationIds?: number[] }[];
      const currentInput = inputs[currentIndex];
      const fieldName = currentInput?.name || `inputs_group_${groupId}_${currentIndex}`;
      const virtualElement: FormElement | null = currentInput ? {
        type: 'input',
        name: currentInput.name || fieldName,
        label: currentInput.label,
        placeholder: currentInput.placeholder,
        value: currentInput.value,
        styles: { inputType: (currentInput.inputType as 'text') || 'text' },
        validations: group.validations?.filter((v) => currentInput.validationIds?.includes(v.id)),
      } : null;
      const value = formValues[fieldName] ?? currentInput?.value ?? '';
      const validationErrorsForCurrent = virtualElement ? await validateField(virtualElement, value, getValidateFieldOptions()) : [];
      if (validationErrorsForCurrent.length > 0) {
        applyFieldValidationResult(fieldName, value, validationErrorsForCurrent);
        const message = validationErrorsForCurrent[0] || 'Please fix the errors before continuing.';
        toast.error(message, { containerId: formToastOptions.containerId, style: formToastOptions.error });
        return;
      }
      setValidationErrors((prev) => {
        const next = { ...prev };
        delete next[fieldName];
        return next;
      });
      setValidatedFieldValues((prev) => ({ ...prev, [fieldName]: value }));
      if (currentIndex < inputs.length - 1 && inputs.length > 0) {
        const duration = group.styles?.inputsGroupAnimationDuration ?? 300;
        const useAnimation = group.styles?.inputsGroupAnimation === 'fade';
        if (useAnimation && duration > 0) {
          setInputsGroupVisible((prev) => ({ ...prev, [groupId]: false }));
          setTimeout(() => {
            setInputsGroupCurrentIndex((prev) => ({ ...prev, [groupId]: currentIndex + 1 }));
            setInputsGroupVisible((prev) => ({ ...prev, [groupId]: true }));
          }, duration);
        } else {
          setInputsGroupCurrentIndex((prev) => ({ ...prev, [groupId]: currentIndex + 1 }));
        }
      }
      // Do not return here so External API (start transaction) and step navigation still run when this button is also the trigger
    }

    const triggerFor = buttonElement.styles?.triggerValidationsFor || [];
    const showStep = buttonElement.styles?.showStep;
    const newErrors: ValidationErrors = { ...validationErrors };
    let hasErrors = false;

    // Validate fields and copy sections if button triggers validations
    for (const key of triggerFor) {
      if (key.startsWith('copy_section:')) {
        const copyEl = getCopySectionByKey(key);
        if (!copyEl || copyEl.type !== 'copy_section') continue;
        const hasRequiredToCopy =
          copyEl.validations?.some((v) => v.type === 'required_to_copy') ?? false;
        if (!hasRequiredToCopy) continue;
        if (!copiedSectionKeys.has(key)) {
          const msg = copyEl.validations?.find((v) => v.type === 'required_to_copy')?.message || 'Please copy the content first';
          newErrors[key] = [msg];
          hasErrors = true;
        } else {
          delete newErrors[key];
        }
        continue;
      }
      const resolved = getElementForFieldName(key);
      if (resolved) {
        const { element: inputElement, fieldName } = resolved;
        const value = formValues[fieldName] || '';
        trackInputFilledIfNeeded(inputElement, fieldName, value);
        const errors = await validateField(inputElement, value, getValidateFieldOptions());
        if (errors.length > 0) {
          newErrors[fieldName] = errors;
          hasErrors = true;
          setValidatedFieldValues((prev) => ({ ...prev, [fieldName]: value }));
        } else {
          delete newErrors[fieldName];
          setValidatedFieldValues((prev) => ({ ...prev, [fieldName]: value }));
        }
      }
    }

    setValidationErrors(newErrors);

    // If validation errors exist: show error as toast popup, disable button, then return
    if (hasErrors) {
      const firstError = Object.values(newErrors).flat()[0] || 'Validation failed';
      const message = buttonElement.styles?.disabledClickMessage?.trim() || firstError;
      toast.error(message, { containerId: formToastOptions.containerId, style: formToastOptions.error });
      setReEnableOnVisibilityChange(buttonElement.styles?.listenVisibilityChange === true);
      return;
    }

    setReEnableOnVisibilityChange(false);

    // Check if this button triggers external API call (compare as numbers in case of string/number mismatch)
    const triggerButtonIds = (() => {
      if (!externalApiSettings) return [];
      if (Array.isArray(externalApiSettings.triggerButtonElementIds) && externalApiSettings.triggerButtonElementIds.length > 0) {
        return externalApiSettings.triggerButtonElementIds.map(Number);
      }
      if (externalApiSettings.triggerButtonElementId != null) {
        return [Number(externalApiSettings.triggerButtonElementId)];
      }
      return [];
    })();
    const buttonId = buttonElement.id != null ? Number(buttonElement.id) : null;
    // Payment-events confirm button should start the transaction even if trigger IDs
    // in External API config are stale after republish (common with snapshots).
    const isConfirmTrackButton =
      paymentEventsEnabled &&
      buttonElement.styles?.paymentPageTrackAction === 'FORM_CONFIRM_BUTTON_CLICKED';
    const shouldStartTransaction =
      Boolean(externalApiSettings?.enabled) &&
      buttonId !== null &&
      form?.id != null &&
      (triggerButtonIds.includes(buttonId) || isConfirmTrackButton);
    if (shouldStartTransaction && form?.id && externalApiSettings) {
      if (!transactionId) {
        toast.error(
          isPayoutMode
            ? 'Payout ID is required. Open the form with a payout_id in the URL.'
            : 'Transaction ID is required. Open the preview with a transaction_id in the URL.',
          { containerId: formToastOptions.containerId, style: formToastOptions.error },
        );
        return;
      }
      if (startTransactionInFlightRef.current) {
        return;
      }
      startTransactionInFlightRef.current = true;
      if (!startTransactionIdempotencyKeyRef.current) {
        startTransactionIdempotencyKeyRef.current =
          typeof crypto !== 'undefined' && typeof crypto.randomUUID === 'function'
            ? crypto.randomUUID()
            : `stx_${Date.now().toString(36)}_${Math.random().toString(36).slice(2, 10)}`;
      }
      try {
        console.log('FormPreviewPage - Executing submit for button:', buttonElement.id, {
          isPayoutMode,
          paymentEventsEnabled,
          socketConnected: isPaymentSocketConnected(),
          preferSocket: !isPayoutMode && (isPublishedPath || paymentEventsEnabled),
          idempotencyKey: startTransactionIdempotencyKeyRef.current,
        });
        const startResult = isPayoutMode
          ? await completePayout({
              formId: form.id,
              payoutId: transactionId,
              formValues,
              fieldMappings: externalApiSettings.fieldMappings,
              idempotencyKey: startTransactionIdempotencyKeyRef.current || undefined,
            })
          : await startTransaction({
              formId: form.id,
              transactionId,
              formValues,
              fieldMappings: externalApiSettings.fieldMappings,
              // Prefer socket when available; if not connected, use default HTTP start-transaction.
              preferSocket: isPublishedPath || paymentEventsEnabled,
              requireSocket: false,
              idempotencyKey: startTransactionIdempotencyKeyRef.current || undefined,
            });
        // Apply status from start response immediately (don't wait only on socket push).
        const resultObj =
          startResult && typeof startResult === 'object'
            ? (startResult as Record<string, unknown>)
            : null;
        const nextStatus =
          (typeof resultObj?.status === 'string' && resultObj.status) ||
          (resultObj?.data &&
          typeof resultObj.data === 'object' &&
          typeof (resultObj.data as { status?: unknown }).status === 'string'
            ? String((resultObj.data as { status: string }).status)
            : null) ||
          (isPayoutMode ? 'PENDING' : 'PROCESSING');
        console.log('FormPreviewPage - submit ok, applying status:', nextStatus);
        setTransactionStatus(nextStatus);
        setTransactionData((prev) =>
          mergeTransactionData(prev, {
            status: nextStatus,
            ...(resultObj || {}),
          }),
        );
        // Disable buttons and inputs per External API config
        const disableButtonIds = Array.isArray(externalApiSettings.disableButtonAfterSubmitElementIds)
          ? externalApiSettings.disableButtonAfterSubmitElementIds.map(Number)
          : [];
        if (disableButtonIds.length > 0) {
          setSubmittedButtonIds((prev) => new Set([...prev, ...disableButtonIds]));
        }
        const inputNamesToDisable =
          externalApiSettings.fieldMappings?.simpleFields
            ?.filter((f) => f.disableInputAfterSubmit && f.inputName)
            .map((f) => f.inputName) ?? [];
        if (inputNamesToDisable.length > 0) {
          setSubmittedInputNames((prev) => new Set([...prev, ...inputNamesToDisable]));
        }
      } catch (error: unknown) {
        console.error('Failed to start transaction via external API:', error);
        const err = error as { response?: { data?: { message?: string } }; message?: string };
        const message = err.response?.data?.message || (error instanceof Error ? error.message : undefined) || err.message || 'Failed to start transaction';
        toast.error(message, { containerId: formToastOptions.containerId, style: formToastOptions.error });
        return;
      } finally {
        startTransactionInFlightRef.current = false;
        startTransactionIdempotencyKeyRef.current = null;
      }
    }

    // If step-by-step mode and no validation errors, navigate to target step
    if (viewMode === 'step_by_step' && showStep !== undefined && steps && steps.length > 0) {
      const sorted = [...steps].sort((a, b) => (a.order ?? 0) - (b.order ?? 0));
      const targetStepIndex = sorted.findIndex((step) => step.id === showStep);
      if (targetStepIndex >= 0 && targetStepIndex !== currentStepIndex) {
        navigateToStep(targetStepIndex);
      }
    }
  };

  const navigateToStep = (stepIndex: number) => {
    if (isTransitioning || stepIndex === currentStepIndex) return;
    
    setIsTransitioning(true);
    
    const animationDuration = stepAnimation === 'none' ? 0 : 300;
    
    setTimeout(() => {
      setCurrentStepIndex(stepIndex);
      setIsTransitioning(false);
    }, animationDuration);
  };

  // When disabled button is clicked: run validation, set errors, scroll to first invalid field, show red popup
  const handleDisabledButtonClick = async (buttonElement: FormElement) => {
    const triggerFor = buttonElement.styles?.triggerValidationsFor || [];
    const newErrors: ValidationErrors = { ...validationErrors };
    let firstInvalidFieldName: string | null = null;

    for (const key of triggerFor) {
      if (key.startsWith('copy_section:')) {
        const copyEl = getCopySectionByKey(key);
        if (!copyEl || copyEl.type !== 'copy_section') continue;
        const hasRequiredToCopy =
          copyEl.validations?.some((v) => v.type === 'required_to_copy') ?? false;
        if (!hasRequiredToCopy) continue;
        if (!copiedSectionKeys.has(key)) {
          const msg = copyEl.validations?.find((v) => v.type === 'required_to_copy')?.message || 'Please copy the content first';
          newErrors[key] = [msg];
          if (firstInvalidFieldName == null) firstInvalidFieldName = key;
        } else {
          delete newErrors[key];
        }
        continue;
      }
      const resolved = getElementForFieldName(key);
      if (resolved) {
        const { element: inputElement, fieldName } = resolved;
        const value = formValues[fieldName] ?? '';
        const errors = await validateField(inputElement, value, getValidateFieldOptions());
        if (errors.length > 0) {
          newErrors[fieldName] = errors;
          if (firstInvalidFieldName == null) firstInvalidFieldName = fieldName;
          setValidatedFieldValues((prev) => ({ ...prev, [fieldName]: value }));
        } else {
          delete newErrors[fieldName];
          setValidatedFieldValues((prev) => ({ ...prev, [fieldName]: value }));
        }
      }
    }

    setValidationErrors(newErrors);

    const firstError = Object.values(newErrors).flat()[0] || 'Validation failed';
    const message = buttonElement.styles?.disabledClickMessage?.trim() || firstError;
    toast.error(message, { containerId: formToastOptions.containerId, style: formToastOptions.error });

    if (firstInvalidFieldName) {
      const scrollTargetId = `field-${firstInvalidFieldName}`;
      setTimeout(() => {
        document.getElementById(scrollTargetId)?.scrollIntoView({ behavior: 'smooth', block: 'center' });
      }, 100);
    }
  };

  const renderElement = (
    element: FormElement,
    index: number,
    componentStyles: ComponentStyle[] = [],
    parentContainer?: FormElement,
    containerIndex?: number,
  ) => {
    // Helper to get common HTML attributes
    const getHtmlAttributes = () => ({
      id: element.htmlId || undefined,
      className: element.className || undefined,
    });

    // Helper to separate text styles from container styles
    const getTextStyles = (allStyles: Record<string, string | number>): Record<string, string | number> => {
      const textStyleKeys = ['color', 'fontSize', 'fontWeight', 'fontFamily', 'lineHeight', 'textAlign', 'textDecoration'];
      const textStyles: Record<string, string | number> = {};
      textStyleKeys.forEach(key => {
        if (allStyles[key] !== undefined && allStyles[key] !== null && allStyles[key] !== '') {
          textStyles[key] = allStyles[key];
        }
      });
      return textStyles;
    };

    const getContainerStyles = (allStyles: Record<string, string | number>): Record<string, string | number> => {
      const textStyleKeys = ['color', 'fontSize', 'fontWeight', 'fontFamily', 'lineHeight', 'textAlign', 'textDecoration'];
      const componentSpecificDisplayKeys = [
        'textWithValueTitleColor', 'textWithValueValueColor',
        'processingStatusProcessingTitleColor', 'processingStatusStatusTitleColor',
        'linkAlign',
      ];
      const containerStyles: Record<string, string | number> = {};
      Object.entries(allStyles).forEach(([key, value]) => {
        if (
          !textStyleKeys.includes(key) &&
          !componentSpecificDisplayKeys.includes(key) &&
          value !== undefined && value !== null && value !== ''
        ) {
          containerStyles[key] = value;
        }
      });
      return containerStyles;
    };

    // Extract CSS-compatible styles from element.styles, excluding non-CSS properties
    // Also merge styles from component style if componentStyleId is present
    // Returns desktop, mobile, and hover styles separately
    const getElementStyles = (): {
      desktop: Record<string, string | number>;
      mobile: Record<string, string | number>;
      hover: Record<string, string | number>;
      disabledButton?: Record<string, string | number>;
      disabledInput?: Record<string, string | number>;
    } => {
      if (!element.styles) return { desktop: {}, mobile: {}, hover: {} };
      
      const nonCssProperties = [
        'validationTriggers',
        'triggerValidationsFor',
        'showStep',
        'componentStyleId',
        'listItems',
        'subtitle',
        'textValue',
        'textWithValueValueFormat',
        'initialTime',
        'timerTimeSource',
        'timerVariableName',
        'timerOnExpiry',
        'timerPosition',
        'timerSvgBefore',
        'showLinkAfterExpiry',
        'linkElementIdAfterExpiry',
        'linkHidden',
        'copyIcon',
        'copyDoneIcon',
        'copySuccessMessage',
        'copySubtitle',
        'copySectionValueSource',
        'copySectionUrlParamKey',
        'valueSource',
        'urlParamKey',
        'sideTextValueSource',
        'sideTextUrlParamKey',
        'timerUrlParamKey',
        'copySectionValueFormat',
        'copySectionValueNumberLocale',
        'copySectionValueNumberMinFractionDigits',
        'copySectionValueNumberMaxFractionDigits',
        'copySectionHideFirstCharacters',
        'textValueSource',
        'textUrlParamKey',
        'textFormat',
        'textNumberLocale',
        'textNumberMinFractionDigits',
        'textNumberMaxFractionDigits',
        'linkIcon',
        'linkHidden',
        'linkAlign',
        'svgCode',
        'svgAlign',
        'uploadSubtitle',
        'hideLabels',
        'modalElements',
        'notificationIcon',
        'notificationDescription',
        'notificationDescriptionType',
        'notificationListItems',
        'processingTitle',
        'processingStatusSvg',
        'statusTitle',
        'containerElements',
        'hideFormName',
        'inputType',
        'selectOptions',
        'listenVisibilityChange',
        'disabledClickMessage',
        'inputsGroupInputs',
        'inputsGroupButtonId',
        'inputsGroupAnimation',
        'inputsGroupAnimationDuration',
        'slideshowImages',
        'slideshowPrevSvg',
        'slideshowNextSvg',
        'slideshowButtonBackground',
        'loaderColor',
        'loaderWidth',
        'loaderHeight',
        'showByUrlParamKey',
        'showByUrlParamValue',
        'showByUrlParamContains',
        'showByVariableKey',
        'showByVariableValue',
        'showByVariableContains',
        'showByVariableSource',
        'hideByVariableKey',
        'hideByVariableValue',
        'hideByVariableContains',
        'hideByVariableSource',
        'hideInputIfValidationCorrect',
        'qrEcLevel',
        'qrSize',
        'qrQuietZone',
        'qrBgColor',
        'qrFgColor',
        'qrLogoImageUrl',
        'qrLogoWidth',
        'qrLogoHeight',
        'qrLogoOpacity',
        'qrLogoPadding',
        'qrLogoPaddingStyle',
        'qrRemoveQrCodeBehindLogo',
        'qrStyle',
        'qrEnableCORS',
        'hidden',
      ];

      // Start with styles from component style if componentStyleId exists
      const componentStyleId = element.styles?.componentStyleId;
      const componentStyle = componentStyleId && componentStyles.length > 0
        ? componentStyles.find((s: ComponentStyle) => s.id === componentStyleId)
        : null;
      
      // If componentStyleId exists, ONLY use styles from component_styles table
      if (componentStyleId && componentStyle && componentStyle.styles) {
        const desktopStyles: Record<string, string | number> = {};
        const mobileStyles: Record<string, string | number> = componentStyle.styles.mobile || {};
        const hoverStyles: Record<string, string | number> = componentStyle.styles.hover || {};
        const disabledButton: Record<string, string | number> = {};
        const disabledInput: Record<string, string | number> = {};
        
        Object.entries(componentStyle.styles).forEach(([key, value]) => {
          if (key === 'mobile' || key === 'hover' || value === null || value === undefined || value === '') return;
          if (key === 'disabledButtonOpacity') { disabledButton.opacity = value as number; return; }
          if (key === 'disabledButtonBackground') { disabledButton.background = value as string; return; }
          if (key === 'disabledButtonBorder') { disabledButton.border = value as string; return; }
          if (key === 'disabledInputOpacity') { disabledInput.opacity = value as number; return; }
          if (key === 'disabledInputBackground') { disabledInput.background = value as string; return; }
          if (key === 'disabledInputBorder') { disabledInput.border = value as string; return; }
          desktopStyles[key] = value as string | number;
        });
        
        return {
          desktop: desktopStyles,
          mobile: mobileStyles,
          hover: hoverStyles,
          disabledButton: Object.keys(disabledButton).length ? disabledButton : undefined,
          disabledInput: Object.keys(disabledInput).length ? disabledInput : undefined,
        };
      }

      // If no componentStyleId, use element's own styles (backward compatibility)
      const desktopStyles: Record<string, string | number> = {};
      const mobileStyles: Record<string, string | number> = (element.styles?.mobile as Record<string, string | number>) || {};
      const hoverStyles: Record<string, string | number> = (element.styles?.hover as Record<string, string | number>) || {};
      
      Object.entries(element.styles || {}).forEach(([key, value]) => {
        if (key !== 'mobile' && key !== 'hover' && !nonCssProperties.includes(key) && value !== null && value !== undefined && value !== '') {
          desktopStyles[key] = value as string | number;
        }
      });

      return { desktop: desktopStyles, mobile: mobileStyles, hover: hoverStyles, disabledButton: undefined, disabledInput: undefined };
    };
    
    // Helper to combine desktop, mobile, and hover styles into MUI sx format
    const combineStyles = (
      desktop: Record<string, string | number>,
      mobile: Record<string, string | number>,
      hover: Record<string, string | number>,
      isButton = false
    ): Record<string, unknown> => {
      const hasMobile = Object.keys(mobile).length > 0;
      const hasHover = isButton && Object.keys(hover).length > 0;
      
      if (!hasMobile && !hasHover) {
        return desktop;
      }
      
      const sx: Record<string, unknown> = { ...desktop };
      
      if (hasMobile) {
        sx['@media (max-width: 767px)'] = mobile;
      }
      
      if (hasHover) {
        sx['&:hover'] = hover;
      }
      
      return sx;
    };
    
    // Helper to combine text/container styles with mobile/hover support
    const getCombinedTextStyles = (
      desktop: Record<string, string | number>,
      mobile: Record<string, string | number>,
      hover: Record<string, string | number>,
      isButton = false
    ) => {
      const desktopText = getTextStyles(desktop);
      const mobileText = getTextStyles(mobile);
      const hoverText = getTextStyles(hover);
      return combineStyles(desktopText, mobileText, hoverText, isButton);
    };
    
    const getCombinedContainerStyles = (
      desktop: Record<string, string | number>,
      mobile: Record<string, string | number>,
      hover: Record<string, string | number>,
      isButton = false
    ) => {
      const desktopContainer = getContainerStyles(desktop);
      const mobileContainer = getContainerStyles(mobile);
      const hoverContainer = getContainerStyles(hover);
      return combineStyles(desktopContainer, mobileContainer, hoverContainer, isButton);
    };

    switch (element.type) {
      case 'input': {
        const initVisibilityKey = getInitVisibilityInputKey(element);
        if (initVisibilityKey && initHiddenInputKeys.has(initVisibilityKey)) {
          return null;
        }
        const fieldName = element.name || `field_${index}`;
        const fieldErrors = validationErrors[fieldName] || [];
        const isDisabledBySubmit = submittedInputNames.has(fieldName);
        const notificationConfig = externalApiSettings?.fieldMappings?.simpleFields?.find(
          (f) => f.inputName === fieldName && f.disableInputAfterSubmit,
        );
        const { desktop, mobile, hover } = getElementStyles();
        const elementStyles = combineStyles(desktop, mobile, hover, false);
        const disabledInputStyles = getElementStyles().disabledInput;
        const inputType = element.styles?.inputType || 'text';
        // Get default value from URL param or DB variables
        const defaultValue = resolveElementMainValue(element, replaceVariables, '');
        // Initialize form value with default if not already set
        const currentValue = formValues[fieldName] !== undefined 
          ? formValues[fieldName] 
          : defaultValue;
        return (
          <Box key={index} sx={{ mb: 2 }} id={element.htmlId || `field-${fieldName}`} className={element.className || undefined}>
            {element.label && (
              <Typography level="body-sm" sx={{ mb: 0.5, fontWeight: 'md' }}>
                {element.label}
              </Typography>
            )}
            <Input
              type={inputType}
              placeholder={element.placeholder || ''}
              name={fieldName}
              value={currentValue}
              onChange={(e) => handleInputChange(element, e.target.value, index)}
              onFocus={() => handleInputFocus(element, fieldName, currentValue)}
              onPaste={(e) => handleInputPaste(element, fieldName, e)}
              onBlur={() => handleInputBlur(element, index)}
              disabled={isDisabledBySubmit}
              sx={{
                width: '100%',
                ...elementStyles,
                ...(isDisabledBySubmit && {
                  ...(disabledInputStyles || {
                    opacity: 0.5,
                    border: '1px solid #119405',
                    background: '#e3eee1',
                  }),
                  '&.Mui-disabled': {
                    ...(disabledInputStyles || {
                      opacity: 0.5,
                      border: '1px solid #119405',
                      background: '#e3eee1',
                    }),
                  },
                }),
                '&.Mui-focused': {
                  borderColor: fieldErrors.length > 0 ? 'danger.500' : 'primary.500',
                },
              }}
              color={fieldErrors.length > 0 ? 'danger' : 'neutral'}
              variant="outlined"
              error={fieldErrors.length > 0}
            />
            {isDisabledBySubmit && (notificationConfig?.notificationText || notificationConfig?.notificationIcon) && (
              <Box
                className="customer-step-info-description-container-success"
                sx={{
                  display: 'flex',
                  alignItems: 'flex-start',
                  gap: '4px',
                  marginTop: '4px',
                  padding: '4px',
                  borderRadius: '4px',
                  background: '#FFDE01',
                }}
              >
                {notificationConfig?.notificationIcon && (
                  <Box
                    className="customer-step-info-description-icon"
                    sx={{ flexShrink: 0, width: 16, height: 17, display: 'flex', alignItems: 'center', justifyContent: 'center' }}
                    dangerouslySetInnerHTML={{ __html: sanitizeSvg(notificationConfig.notificationIcon) }}
                  />
                )}
                {notificationConfig?.notificationText && (
                  <Typography
                    className="customer-step-info-description"
                    sx={{
                      color: '#18191A',
                      fontFeatureSettings: "'liga' off, 'clig' off",
                      fontSize: '12px',
                      fontStyle: 'normal',
                      fontWeight: 400,
                      lineHeight: '14.4px',
                    }}
                  >
                    {notificationConfig.notificationText}
                  </Typography>
                )}
              </Box>
            )}
            {fieldErrors.length > 0 && (
              <Box sx={{ mt: 1 }}>
                {fieldErrors.map((error, errorIndex) => (
                  <Alert
                    key={errorIndex}
                    color="danger"
                    size="sm"
                    startDecorator={<ErrorOutline />}
                    sx={{
                      mt: errorIndex > 0 ? 0.5 : 0,
                      fontSize: '0.875rem',
                      fontWeight: 500,
                    }}
                  >
                    {error}
                  </Alert>
                ))}
              </Box>
            )}
          </Box>
        );
      }
      case 'select': {
        const fieldName = element.name || `field_${index}`;
        const fieldErrors = validationErrors[fieldName] || [];
        const { desktop, mobile, hover } = getElementStyles();
        const elementStyles = combineStyles(desktop, mobile, hover, false);
        const options = element.styles?.selectOptions || [];
        const currentValue = formValues[fieldName] !== undefined
          ? formValues[fieldName]
          : (options[0]?.value ?? '');
        return (
          <Box key={index} sx={{ mb: 2 }} id={element.htmlId || `field-${fieldName}`} className={element.className || undefined}>
            {element.label && (
              <Typography level="body-sm" sx={{ mb: 0.5, fontWeight: 'md' }}>
                {element.label}
              </Typography>
            )}
            <Select
              value={currentValue}
              onChange={(_event, value) => handleInputChange(element, value ?? '', index)}
              sx={{
                width: '100%',
                ...elementStyles,
                '&.Mui-focused': {
                  borderColor: fieldErrors.length > 0 ? 'danger.500' : 'primary.500',
                },
              }}
              color={fieldErrors.length > 0 ? 'danger' : 'neutral'}
              variant="outlined"
            >
              <Option value="">— Select —</Option>
              {options.map((opt, i) => (
                <Option key={i} value={opt.value}>
                  {opt.label || opt.value}
                </Option>
              ))}
            </Select>
            {fieldErrors.length > 0 && (
              <Box sx={{ mt: 1 }}>
                {fieldErrors.map((error, errorIndex) => (
                  <Alert
                    key={errorIndex}
                    color="danger"
                    size="sm"
                    startDecorator={<ErrorOutline />}
                    sx={{
                      mt: errorIndex > 0 ? 0.5 : 0,
                      fontSize: '0.875rem',
                      fontWeight: 500,
                    }}
                  >
                    {error}
                  </Alert>
                ))}
              </Box>
            )}
          </Box>
        );
      }
      case 'inputs_group': {
        const inputs = element.styles?.inputsGroupInputs || [];
        const groupId = element.id != null ? Number(element.id) : 0;
        const currentIndex = Math.min(inputsGroupCurrentIndex[groupId] ?? 0, Math.max(0, inputs.length - 1));
        const visible = inputsGroupVisible[groupId] !== false;
        const duration = element.styles?.inputsGroupAnimationDuration ?? 300;
        const useAnimation = element.styles?.inputsGroupAnimation === 'fade';

        if (inputs.length === 0) {
          return (
            <Box key={index} sx={{ mb: 2 }} {...getHtmlAttributes()}>
              <Typography level="body-sm" sx={{ color: 'text.tertiary' }}>No inputs in group</Typography>
            </Box>
          );
        }

        const currentInput = inputs[currentIndex];
        const fieldName = currentInput.name || `inputs_group_${groupId}_${currentIndex}`;
        const fieldErrors = validationErrors[fieldName] || [];
        const isDisabledBySubmit = submittedInputNames.has(fieldName);
        const notificationConfig = externalApiSettings?.fieldMappings?.simpleFields?.find(
          (f) => f.inputName === fieldName && f.disableInputAfterSubmit,
        );
        const currentValue = formValues[fieldName] !== undefined ? formValues[fieldName] : (currentInput.value ?? '');
        const virtualElement: FormElement = {
          type: 'input',
          name: currentInput.name || fieldName,
          label: currentInput.label,
          placeholder: currentInput.placeholder,
          value: currentInput.value,
          styles: { inputType: (currentInput.inputType as 'text') || 'text' },
          validations: element.validations?.filter((v) => currentInput.validationIds?.includes(v.id)),
        };
        const { desktop, mobile, hover } = getElementStyles();
        const elementStyles = combineStyles(desktop, mobile, hover, false);
        const disabledInputStyles = getElementStyles().disabledInput;

        return (
          <Box
            key={index}
            sx={{
              mb: 2,
              opacity: visible ? 1 : 0,
              transition: useAnimation ? `opacity ${duration}ms ease-in-out` : 'none',
            }}
            {...getHtmlAttributes()}
          >
            {currentInput.label && (
              <Typography level="body-sm" sx={{ mb: 0.5, fontWeight: 'md' }}>
                {currentInput.label}
              </Typography>
            )}
            <Input
              type={(currentInput.inputType as 'text') || 'text'}
              placeholder={currentInput.placeholder || ''}
              name={fieldName}
              value={currentValue}
              onChange={(e) => handleInputChange(virtualElement, e.target.value, 0)}
              onFocus={() => handleInputFocus(virtualElement, fieldName, currentValue)}
              onPaste={(e) => handleInputPaste(virtualElement, fieldName, e)}
              onBlur={() => handleInputBlur(virtualElement, 0)}
              disabled={isDisabledBySubmit}
              sx={{
                width: '100%',
                ...elementStyles,
                ...(isDisabledBySubmit && {
                  ...(disabledInputStyles || {
                    opacity: 0.5,
                    border: '1px solid #119405',
                    background: '#e3eee1',
                  }),
                  '&.Mui-disabled': {
                    ...(disabledInputStyles || {
                      opacity: 0.5,
                      border: '1px solid #119405',
                      background: '#e3eee1',
                    }),
                  },
                }),
                '&.Mui-focused': {
                  borderColor: fieldErrors.length > 0 ? 'danger.500' : 'primary.500',
                },
              }}
              color={fieldErrors.length > 0 ? 'danger' : 'neutral'}
              variant="outlined"
              error={fieldErrors.length > 0}
            />
            {isDisabledBySubmit && (notificationConfig?.notificationText || notificationConfig?.notificationIcon) && (
              <Box
                className="customer-step-info-description-container-success"
                sx={{
                  display: 'flex',
                  alignItems: 'flex-start',
                  gap: '4px',
                  marginTop: '4px',
                  padding: '4px',
                  borderRadius: '4px',
                  background: '#FFDE01',
                }}
              >
                {notificationConfig?.notificationIcon && (
                  <Box
                    className="customer-step-info-description-icon"
                    sx={{ flexShrink: 0, width: 16, height: 17, display: 'flex', alignItems: 'center', justifyContent: 'center' }}
                    dangerouslySetInnerHTML={{ __html: sanitizeSvg(notificationConfig.notificationIcon) }}
                  />
                )}
                {notificationConfig?.notificationText && (
                  <Typography
                    className="customer-step-info-description"
                    sx={{
                      color: '#18191A',
                      fontFeatureSettings: "'liga' off, 'clig' off",
                      fontSize: '12px',
                      fontStyle: 'normal',
                      fontWeight: 400,
                      lineHeight: '14.4px',
                    }}
                  >
                    {notificationConfig.notificationText}
                  </Typography>
                )}
              </Box>
            )}
            {fieldErrors.length > 0 && (
              <Box sx={{ mt: 1 }}>
                {fieldErrors.map((err, errIdx) => (
                  <Alert
                    key={errIdx}
                    color="danger"
                    size="sm"
                    startDecorator={<ErrorOutline />}
                    sx={{ mt: errIdx > 0 ? 0.5 : 0, fontSize: '0.875rem', fontWeight: 500 }}
                  >
                    {err}
                  </Alert>
                ))}
              </Box>
            )}
          </Box>
        );
      }
      case 'button': {
        const { desktop, mobile, hover } = getElementStyles();
        const elementStyles = combineStyles(desktop, mobile, hover, true);
        const triggerFor = element.styles?.triggerValidationsFor || [];
        const hasValidations = triggerFor.length > 0;
        let triggeredFieldsPass = true;
        if (hasValidations) {
          for (const key of triggerFor) {
            if (key.startsWith('copy_section:')) {
              const copyEl = getCopySectionByKey(key);
              if (!copyEl || copyEl.type !== 'copy_section') continue;
              const hasRequiredToCopy =
                copyEl.validations?.some((v) => v.type === 'required_to_copy') ?? false;
              if (hasRequiredToCopy && !copiedSectionKeys.has(key)) {
                triggeredFieldsPass = false;
                break;
              }
              continue;
            }
            const resolved = getElementForFieldName(key);
            if (resolved) {
              const { element: inputElement, fieldName } = resolved;
              const value = formValues[fieldName] ?? '';
              const errors = getFieldErrorsForButtonState(
                inputElement,
                fieldName,
                value,
                validationErrors,
                validatedFieldValues,
              );
              if (errors.length > 0) {
                triggeredFieldsPass = false;
                break;
              }
            }
          }
        }
        const listenVisibilityChange = element.styles?.listenVisibilityChange === true;
        const disabledByVisibility = listenVisibilityChange && !isPageVisibleOrFocused;
        const isDisabledBySubmit = element.id != null && submittedButtonIds.has(Number(element.id));
        const isDisabled = isDisabledBySubmit || (hasValidations && !triggeredFieldsPass) || disabledByVisibility;
        const disabledButtonStyles = getElementStyles().disabledButton ?? {};
        return (
          <Box
            key={index}
            {...getHtmlAttributes()}
            sx={{ mb: 2, ...(isDisabled && { cursor: 'pointer' }) }}
            onClick={(e) => {
              if (isDisabled) {
                e.preventDefault();
                if (disabledByVisibility && !(hasValidations && !triggeredFieldsPass) && !isDisabledBySubmit) {
                  const visibilityMessage = element.styles?.disabledClickMessage?.trim() || 'Please return to this tab or focus the window to enable this button.';
                  toast.error(visibilityMessage, { containerId: formToastOptions.containerId, style: formToastOptions.error });
                  return;
                }
                handleDisabledButtonClick(element);
              }
            }}
          >
            <Button
              variant="solid"
              color="primary"
              disabled={isDisabled}
              sx={{
                width: '100%',
                ...elementStyles,
                ...(isDisabled && {
                  ...disabledButtonStyles,
                  opacity: disabledButtonStyles.opacity ?? 0.65,
                  cursor: 'pointer',
                  pointerEvents: 'none',
                  '&.Mui-disabled': {
                    ...disabledButtonStyles,
                    opacity: disabledButtonStyles.opacity ?? 0.65,
                  },
                }),
              }}
              onClick={() => handleButtonClick(element)}
            >
              {element.label || 'Button'}
            </Button>
          </Box>
        );
      }
      case 'text': {
        const { desktop, mobile, hover } = getElementStyles();
        const textStyles = getCombinedTextStyles(desktop, mobile, hover, false);
        const containerStyles = getCombinedContainerStyles(desktop, mobile, hover, false);
        const rawValue = resolveElementMainValue(element, replaceVariables, 'Sample text');
        const textFormat = element.styles?.textFormat ?? 'none';
        const locale = element.styles?.textNumberLocale || 'de-DE';
        const minFrac = element.styles?.textNumberMinFractionDigits;
        const maxFrac = element.styles?.textNumberMaxFractionDigits;
        const displayValue = textFormat === 'number'
          ? formatFirstNumberInText(rawValue, locale, minFrac, maxFrac)
          : rawValue;
        return (
          <Box key={index} sx={{ mb: 2, ...containerStyles }} {...getHtmlAttributes()}>
            <Typography level="body-md" sx={textStyles}>
              {displayValue}
            </Typography>
          </Box>
        );
      }
      case 'image': {
        const { desktop, mobile, hover } = getElementStyles();
        const textStyles = getCombinedTextStyles(desktop, mobile, hover, false);
        const containerStyles = getCombinedContainerStyles(desktop, mobile, hover, false);
        // Extract textAlign for image centering
        const imageAlign = textStyles.textAlign || containerStyles.textAlign || 'left';
        return (
          <Box key={index} sx={{ mb: 2, textAlign: imageAlign as 'left' | 'right' | 'center' | 'justify' }} {...getHtmlAttributes()}>
            {element.label && (
              <Typography level="body-sm" sx={{ mb: 0.5, fontWeight: 'md', ...textStyles }}>
                {element.label}
              </Typography>
            )}
            <Box
              component="img"
              src={element.value || 'https://via.placeholder.com/300x200?text=Image'}
              alt={element.label || 'Image'}
              sx={{
                maxWidth: '100%',
                height: 'auto',
                borderRadius: 'sm',
                border: '1px solid',
                borderColor: 'divider',
                objectFit: 'contain',
                ...containerStyles,
                // Remove textAlign from containerStyles as it's applied to parent Box
                textAlign: undefined,
              }}
            />
          </Box>
        );
      }
      case 'list': {
        const listItems = element.styles?.listItems || [];
        const subtitle = element.styles?.subtitle || '';
        const { desktop, mobile, hover } = getElementStyles();
        const textStyles = getCombinedTextStyles(desktop, mobile, hover, false);
        const containerStyles = getCombinedContainerStyles(desktop, mobile, hover, false);
        return (
          <Box key={index} sx={{ mb: 2, ...containerStyles }} {...getHtmlAttributes()}>
            {element.label && (
              <Typography level="title-md" sx={{ mb: 0.5, fontWeight: 'md' }}>
                {element.label}
              </Typography>
            )}
            {subtitle && (
              <Typography level="body-sm" sx={{ mb: 1.5, color: 'text.secondary' }}>
                {subtitle}
              </Typography>
            )}
            {listItems.length > 0 ? (
              <Box component="ul" sx={{ m: 0, pl: 2 }}>
                {listItems.map((item, idx) => (
                  <Typography key={idx} component="li" level="body-sm" sx={{ mb: 0.5, ...textStyles }}>
                    {item}
                  </Typography>
                ))}
              </Box>
            ) : (
              <Typography level="body-sm" sx={{ color: 'text.tertiary', fontStyle: 'italic' }}>
                No items in list
              </Typography>
            )}
          </Box>
        );
      }
      case 'text_with_value': {
        const textValue = resolveSideTextValue(element, replaceVariables, '');
        const valueReplaced = resolveElementMainValue(element, replaceVariables, '');
        const valueFormat = element.styles?.textWithValueValueFormat ?? 'none';
        const valueLocale = element.styles?.textNumberLocale || 'de-DE';
        const valueMinFrac = element.styles?.textNumberMinFractionDigits;
        const valueMaxFrac = element.styles?.textNumberMaxFractionDigits;
        const value =
          valueFormat === 'phone'
            ? formatPhoneDisplayValue(valueReplaced)
            : valueFormat === 'number'
              ? formatFirstNumberInText(valueReplaced, valueLocale, valueMinFrac, valueMaxFrac)
              : valueReplaced;
        const { desktop, mobile, hover } = getElementStyles();
        const textStyles = getCombinedTextStyles(desktop, mobile, hover, false);
        const containerStyles = getCombinedContainerStyles(desktop, mobile, hover, false);
        const titleColor = (desktop as Record<string, string | number>).textWithValueTitleColor;
        const valueColor = (desktop as Record<string, string | number>).textWithValueValueColor;
        return (
          <Box key={index} sx={{ mb: 2, display: 'flex', justifyContent: 'space-between', alignItems: 'center', width: '100%', ...containerStyles }} {...getHtmlAttributes()}>
            <Typography level="body-md" sx={{ flex: 1, ...textStyles, ...(titleColor != null && titleColor !== '' && { color: String(titleColor) }) }}>
              {textValue || 'Text'}
            </Typography>
            <Typography level="body-md" sx={{ fontWeight: 'md', ml: 2, textAlign: 'right', ...textStyles, ...(valueColor != null && valueColor !== '' && { color: String(valueColor) }) }}>
              {value || 'Value'}
            </Typography>
          </Box>
        );
      }
      case 'timer': {
        const timerPosition = element.styles?.timerPosition ?? 'inline';
        if (timerPosition === 'top-left' || timerPosition === 'top' || timerPosition === 'top-right') {
          return null;
        }
        const onExpiry = element.styles?.timerOnExpiry ?? 'show_00';
        const linkElementIdAfterExpiry = element.styles?.linkElementIdAfterExpiry;
        const showLinkAfterExpiry = onExpiry === 'show_link' && linkElementIdAfterExpiry && revealedLinkIds.has(Number(linkElementIdAfterExpiry));
        if (showLinkAfterExpiry) {
          const linkEl = getElementById(Number(linkElementIdAfterExpiry));
          if (linkEl?.type === 'link' && linkEl.styles?.linkHidden) {
            return null;
          }
          if (linkEl?.type === 'link') {
            const linkUrl = sanitizeUrl(resolveElementMainValue(linkEl, replaceVariables, '#'), '#');
            const linkTitle = replaceVariables(linkEl.label || 'Link');
            const linkIcon = sanitizeSvg(linkEl.styles?.linkIcon || '');
            const hideLinkText = linkEl.styles?.hideLinkText || false;
            const linkTarget = linkEl.styles?.linkTarget ?? '_blank';
            const { desktop: linkDesktop, mobile: linkMobile, hover: linkHover } = getElementStylesWithVariants(linkEl, componentStyles);
            const linkAlign = (linkDesktop.linkAlign as 'left' | 'center' | 'right') ?? linkEl.styles?.linkAlign ?? 'left';
            const linkJustifyContent = linkAlign === 'center' ? 'center' : linkAlign === 'right' ? 'flex-end' : 'flex-start';
            const linkTextStyles = getCombinedTextStyles(linkDesktop, linkMobile, linkHover, false);
            const linkContainerStyles = getCombinedContainerStyles(linkDesktop, linkMobile, linkHover, false);
            const linkHoverStyles = typeof (linkTextStyles as Record<string, unknown>)?.['&:hover'] === 'object' ? (linkTextStyles as Record<string, unknown>)['&:hover'] as Record<string, unknown> : {};
            return (
              <Box key={index} sx={{ mb: 2, display: 'flex', justifyContent: linkJustifyContent }}>
                <Box
                  component="a"
                  href={linkUrl}
                  target={linkTarget || '_blank'}
                  rel={linkTarget === '_blank' ? 'noopener noreferrer' : undefined}
                  sx={{
                    display: 'inline-flex',
                    alignItems: 'center',
                    gap: 1,
                    textDecoration: 'none',
                    ...linkContainerStyles,
                    ...linkTextStyles,
                    color: (linkTextStyles as Record<string, unknown>)?.color ?? 'primary.500',
                    '&:hover': { ...linkHoverStyles, textDecoration: 'underline' },
                  }}
                >
                  {linkIcon && (
                    <Box sx={{ width: 20, height: 20, display: 'flex', alignItems: 'center', justifyContent: 'center', flexShrink: 0 }} dangerouslySetInnerHTML={{ __html: sanitizeSvg(linkIcon) }} />
                  )}
                  {!hideLinkText && (
                    <Typography component="span" level="body-md" sx={{ color: 'inherit' }}>
                      {linkTitle}
                    </Typography>
                  )}
                </Box>
              </Box>
            );
          }
          return null;
        }
        const onExpiryNavigateToErrorStep =
          viewMode === 'step_by_step' && steps && steps.length > 0 && form?.errorStepId != null
            ? () => {
                const idx = steps.findIndex((s) => s.id === form?.errorStepId);
                if (idx >= 0) navigateToStep(idx);
              }
            : undefined;
        return (
          <TimerElement
            element={element}
            componentStyles={componentStyles}
            replaceVariables={replaceVariables}
            transactionData={transactionData ?? undefined}
            onExpiryNavigateToErrorStep={onExpiryNavigateToErrorStep}
            onExpiryShowLink={
              onExpiry === 'show_link' && linkElementIdAfterExpiry
                ? (linkId) => setRevealedLinkIds((prev) => new Set(prev).add(linkId))
                : undefined
            }
          />
        );
      }
      case 'svg': {
        const svgCode = sanitizeSvg(element.styles?.svgCode || '');
        const svgAlign = element.styles?.svgAlign || 'left';
        const { desktop, mobile, hover } = getElementStyles();
        const containerStyles = getCombinedContainerStyles(desktop, mobile, hover, false);
        return (
          <Box
            key={index}
            sx={{
              mb: 2,
              display: 'flex',
              justifyContent: svgAlign === 'center' ? 'center' : svgAlign === 'right' ? 'flex-end' : 'flex-start',
              '& svg': { display: 'block', maxWidth: '100%', height: 'auto' },
              ...containerStyles,
            }}
            {...getHtmlAttributes()}
          >
            {svgCode ? <Box component="span" dangerouslySetInnerHTML={{ __html: sanitizeSvg(svgCode) }} /> : null}
          </Box>
        );
      }
      case 'qr_code': {
        return (
          <QrCodeFormElement
            key={index}
            element={element}
            componentStyles={componentStyles}
            replaceVariables={replaceVariables}
          />
        );
      }
      case 'copy_section': {
        const copyKey =
          parentContainer != null && containerIndex != null
            ? `copy_section:container_${parentContainer.id}_${containerIndex}`
            : element.id != null
              ? `copy_section:${element.id}`
              : undefined;
        return (
          <CopySectionElement
            element={element}
            componentStyles={componentStyles}
            replaceVariables={replaceVariables}
            onCopy={copyKey != null ? () => setCopiedSectionKeys((prev) => new Set(prev).add(copyKey)) : undefined}
            onTrackCopy={() => {
              const action = element.styles?.paymentPageTrackAction;
              if (action) {
                trackPaymentEvent(action as PaymentPageTrackAction, {
                  element_id: element.id,
                  copy_label: element.label,
                  copied_value: resolveElementMainValue(element, replaceVariables, ''),
                });
              }
            }}
            toastOptions={formToastOptions}
          />
        );
      }
      case 'link': {
        if (element.styles?.linkHidden) {
          const linkId = element.id != null ? Number(element.id) : NaN;
          if (!Number.isFinite(linkId) || !revealedLinkIds.has(linkId)) {
            return null;
          }
        }
        const linkUrl = sanitizeUrl(resolveElementMainValue(element, replaceVariables, '#'), '#');
        const linkTitleRaw = element.label || 'Link';
        const linkTitle = replaceVariables(linkTitleRaw);
        const linkIcon = sanitizeSvg(element.styles?.linkIcon || '');
        const hideLinkText = element.styles?.hideLinkText || false;
        const linkTarget = element.styles?.linkTarget ?? '_blank';
        const { desktop, mobile, hover } = getElementStyles();
        const linkAlign = (desktop.linkAlign as 'left' | 'center' | 'right') ?? element.styles?.linkAlign ?? 'left';
        const textStyles = getCombinedTextStyles(desktop, mobile, hover, false);
        const containerStyles = getCombinedContainerStyles(desktop, mobile, hover, false);
        const linkHoverStyles = typeof (textStyles as Record<string, unknown>)?.['&:hover'] === 'object' ? (textStyles as Record<string, unknown>)['&:hover'] as Record<string, unknown> : {};
        const justifyContent = linkAlign === 'center' ? 'center' : linkAlign === 'right' ? 'flex-end' : 'flex-start';
        return (
          <Box key={index} sx={{ mb: 2, display: 'flex', justifyContent }} {...getHtmlAttributes()}>
            <Box
              component="a"
              href={linkUrl}
              target={linkTarget || '_blank'}
              rel={linkTarget === '_blank' ? 'noopener noreferrer' : undefined}
              sx={{
                display: 'inline-flex',
                alignItems: 'center',
                gap: 1,
                textDecoration: 'none',
                ...containerStyles,
                ...textStyles,
                color: (textStyles as Record<string, unknown>)?.color ?? 'primary.500',
                '&:hover': {
                  ...linkHoverStyles,
                  textDecoration: 'underline',
                },
              }}
            >
              {linkIcon && (
                <Box
                  sx={{
                    width: 20,
                    height: 20,
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'center',
                    flexShrink: 0,
                  }}
                  dangerouslySetInnerHTML={{ __html: sanitizeSvg(linkIcon) }}
                />
              )}
              {!hideLinkText && (
                <Typography component="span" level="body-md" sx={{ color: 'inherit' }}>
                  {linkTitle}
                </Typography>
              )}
            </Box>
          </Box>
        );
      }
      case 'upload_file': {
        return <UploadFileElement key={index} element={element} componentStyles={componentStyles} toastOptions={formToastOptions} />;
      }
      case 'modal': {
        const ModalElement: React.FC<{ element: FormElement }> = ({ element }) => {
          const [open, setOpen] = useState(false);
          const modalElements = element.styles?.modalElements || [];
          const { desktop, mobile, hover } = getElementStyles();
          const elementStyles = combineStyles(desktop, mobile, hover, true);

          return (
            <Box {...getHtmlAttributes()}>
              <Button
                variant="outlined"
                onClick={() => setOpen(true)}
                sx={{ mb: 2, ...elementStyles }}
              >
                {element.label || 'Open Modal'}
              </Button>
              <Modal open={open} onClose={() => setOpen(false)}>
                <ModalDialog sx={{ maxWidth: 600, width: '90%' }}>
                  <ModalClose />
                  <Typography level="title-lg" sx={{ mb: 2 }}>
                    {element.label || 'Modal'}
                  </Typography>
                  <Stack spacing={0}>
                    {modalElements.length === 0 ? (
                      <Typography level="body-sm" sx={{ color: 'text.tertiary' }}>
                        No elements in modal
                      </Typography>
                    ) : (
                      modalElements.map((modalEl, modalIdx) => {
                        const modalElementKey = modalEl.id || `modal_element_${modalIdx}`;
                        return (
                          <Box key={modalElementKey}>
                            {renderElement(modalEl, modalIdx, componentStyles)}
                          </Box>
                        );
                      })
                    )}
                  </Stack>
                </ModalDialog>
              </Modal>
            </Box>
          );
        };

        return <ModalElement key={index} element={element} />;
      }
      case 'notification_message': {
        const notificationIcon = element.styles?.notificationIcon || '';
        const notificationDescription = element.styles?.notificationDescription || '';
        const notificationDescriptionType = element.styles?.notificationDescriptionType || 'text';
        const notificationListItems = element.styles?.notificationListItems || [];
        const hasDescription = notificationDescriptionType === 'text' 
          ? notificationDescription 
          : notificationListItems.length > 0;
        const { desktop, mobile, hover } = getElementStyles();
        const textStyles = getCombinedTextStyles(desktop, mobile, hover, false);
        const containerStyles = getCombinedContainerStyles(desktop, mobile, hover, false);
        
        return (
          <Box key={index} {...getHtmlAttributes()}>
            <Box
              sx={{
                p: 2,
                border: '1px solid',
                borderColor: 'divider',
                borderRadius: 'sm',
                bgcolor: 'background.level1',
                ...containerStyles,
              }}
            >
              <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: hasDescription ? 1 : 0 }}>
                {notificationIcon && (
                  <Box
                    sx={{
                      width: 20,
                      height: 20,
                      display: 'flex',
                      alignItems: 'center',
                      justifyContent: 'center',
                      flexShrink: 0,
                    }}
                    dangerouslySetInnerHTML={{ __html: sanitizeSvg(notificationIcon) }}
                  />
                )}
                <Typography level="title-sm" sx={{ fontWeight: 'md', ...textStyles }}>
                  {element.label || 'Notification Title'}
                </Typography>
              </Box>
              {notificationDescriptionType === 'text' && notificationDescription && (
                <Typography level="body-sm" sx={{ color: 'text.secondary', ...textStyles }}>
                  {notificationDescription}
                </Typography>
              )}
              {notificationDescriptionType === 'list' && notificationListItems.length > 0 && (
                <Box component="ul" sx={{ m: 0, pl: 2 }}>
                  {notificationListItems.map((item, idx) => (
                    <Typography key={idx} component="li" level="body-sm" sx={{ mb: 0.5, color: 'text.secondary', ...textStyles }}>
                      {item}
                    </Typography>
                  ))}
                </Box>
              )}
            </Box>
          </Box>
        );
      }
      case 'processing_status': {
        const processingTitle = element.styles?.processingTitle || 'Payment Status';
        const statusTitle = element.styles?.statusTitle || 'In Progress';
        const svg = element.styles?.processingStatusSvg || '';
        const { desktop, mobile, hover } = getElementStyles();
        const textStyles = getCombinedTextStyles(desktop, mobile, hover, false);
        const containerStyles = getCombinedContainerStyles(desktop, mobile, hover, false);
        const processingTitleColor = (desktop as Record<string, string | number>).processingStatusProcessingTitleColor;
        const statusTitleColor = (desktop as Record<string, string | number>).processingStatusStatusTitleColor;
        return (
          <Box key={index} sx={{ mb: 2 }} {...getHtmlAttributes()}>
            <Box
              sx={{
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'space-between',
                p: 2,
                border: '1px solid',
                borderColor: 'divider',
                borderRadius: 'sm',
                bgcolor: 'background.level1',
                ...containerStyles,
              }}
            >
              <Typography level="title-sm" sx={{ fontWeight: 'md', ...textStyles, ...(processingTitleColor != null && processingTitleColor !== '' && { color: String(processingTitleColor) }) }}>
                {processingTitle}
              </Typography>
              <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                {svg && (
                  <Box
                    sx={{
                      width: 24,
                      height: 24,
                      display: 'flex',
                      alignItems: 'center',
                      justifyContent: 'center',
                      flexShrink: 0,
                      animation: 'processing-spin 1s linear infinite',
                      '@keyframes processing-spin': {
                        from: { transform: 'rotate(0deg)' },
                        to: { transform: 'rotate(360deg)' },
                      },
                    }}
                    dangerouslySetInnerHTML={{ __html: sanitizeSvg(svg) }}
                  />
                )}
                <Typography level="title-sm" sx={{ fontWeight: 'md', ...textStyles, ...(statusTitleColor != null && statusTitleColor !== '' ? { color: String(statusTitleColor) } : { color: 'warning.500' }) }}>
                  {statusTitle}
                </Typography>
              </Box>
            </Box>
          </Box>
        );
      }
      case 'loader': {
        const color = element.styles?.loaderColor ?? 'rgb(11, 111, 199)';
        const width = element.styles?.loaderWidth ?? '50px';
        const height = element.styles?.loaderHeight ?? '50px';
        const { desktop, mobile, hover } = getElementStyles();
        const containerStyles = getCombinedContainerStyles(desktop, mobile, hover, false);
        return (
          <Box key={index} sx={{ mb: 2, display: 'flex', justifyContent: 'center', ...containerStyles }} {...getHtmlAttributes()}>
            <Box
              className="loader"
              sx={{
                width,
                height,
                border: '4px solid',
                borderColor: '#e0e0e0',
                borderTopColor: color,
                borderRadius: '50%',
                animation: 'loader-spin 1s linear infinite',
                '@keyframes loader-spin': {
                  from: { transform: 'rotate(0deg)' },
                  to: { transform: 'rotate(360deg)' },
                },
              }}
            />
          </Box>
        );
      }
      case 'slideshow': {
        const { desktop, mobile, hover } = getElementStyles();
        let containerStyles = getCombinedContainerStyles(desktop, mobile, hover, false);
        containerStyles = { ...containerStyles };
        delete (containerStyles as Record<string, unknown>).slideshowViewportBorder;
        delete (containerStyles as Record<string, unknown>).slideshowViewportPadding;
        delete (containerStyles as Record<string, unknown>).slideshowViewportBackground;
        const viewportStyles: Record<string, unknown> = {};
        if (desktop && (desktop.slideshowViewportBorder != null || desktop.slideshowViewportPadding != null || desktop.slideshowViewportBackground != null)) {
          if (desktop.slideshowViewportBorder != null) viewportStyles.border = desktop.slideshowViewportBorder;
          if (desktop.slideshowViewportPadding != null) viewportStyles.padding = desktop.slideshowViewportPadding;
          if (desktop.slideshowViewportBackground != null) viewportStyles.bgcolor = desktop.slideshowViewportBackground;
        }
        const buttonBg = element.styles?.slideshowButtonBackground ?? 'rgb(11, 111, 199)';
        const slideId = element.htmlId || `slideshow-${index}-${element.id ?? 0}`;
        return (
          <SlideshowElement
            key={index}
            element={element}
            slideId={slideId}
            containerStyles={containerStyles}
            viewportStyles={viewportStyles}
            buttonBackground={buttonBg}
            htmlAttributes={getHtmlAttributes()}
          />
        );
      }
      case 'container': {
        const containerElements = element.styles?.containerElements || [];
        const { desktop, mobile, hover } = getElementStyles();
        const elementStyles = combineStyles(desktop, mobile, hover, false);
        return (
          <Box key={index} sx={{ mb: 2 }} {...getHtmlAttributes()}>
            {element.label && (
              <Typography level="body-sm" sx={{ mb: 1, fontWeight: 'md' }}>
                {element.label}
              </Typography>
            )}
            <Box
              sx={{
                p: 2,
                border: '1px solid',
                borderColor: 'divider',
                borderRadius: 'sm',
                bgcolor: 'background.level1',
                ...elementStyles,
              }}
            >
              {containerElements.length === 0 ? (
                <Typography level="body-sm" sx={{ color: 'text.tertiary', fontStyle: 'italic', textAlign: 'center' }}>
                  No elements in container
                </Typography>
              ) : (
                <Stack spacing={0}>
                  {containerElements
                    .map((containerEl, containerIdx) => ({ containerEl, containerIdx }))
                    .filter(
                      ({ containerEl: el }) =>
                        !el.styles?.hidden &&
                        isElementVisibleByUrlParam(el) &&
                        isElementVisibleByVariable(el, transactionData) &&
                        !isElementHiddenByVariable(el, transactionData),
                    )
                    .map(({ containerEl, containerIdx }) => {
                      const containerElementKey = containerEl.id || `container_element_${containerIdx}`;
                      return (
                        <Box key={containerElementKey}>
                          {renderElement(containerEl, containerIdx, componentStyles, element, containerIdx)}
                        </Box>
                      );
                    })}
                </Stack>
              )}
            </Box>
          </Box>
        );
      }
      case 'step_progress_bar': {
        if (!steps || steps.length === 0) {
          const { desktop, mobile, hover } = getElementStyles();
          const elementStyles = combineStyles(desktop, mobile, hover, false);
          return (
            <Box key={index} sx={{ mb: 2, ...elementStyles }} {...getHtmlAttributes()}>
              <Typography level="body-sm" sx={{ color: 'text.tertiary', textAlign: 'center' }}>
                No steps available
              </Typography>
            </Box>
          );
        }

        const sortedSteps = [...steps].sort((a, b) => (a.order ?? 0) - (b.order ?? 0));
        const currentStepNumber = currentStepIndex + 1;
        const totalSteps = sortedSteps.length;
        const hideLabels = element.styles?.hideLabels || false;
        const { desktop, mobile, hover } = getElementStyles();
        const elementStyles = combineStyles(desktop, mobile, hover, false);

        const stepBarComponentStyleId = element.styles?.componentStyleId;
        const stepBarStyle = stepBarComponentStyleId && componentStyles.length > 0
          ? componentStyles.find((s: ComponentStyle) => s.id === stepBarComponentStyleId)
          : null;
        const ss = stepBarStyle?.styles || {};
        const activeBg = ss.stepProgressBarActiveBg ?? 'primary.500';
        const completedBg = ss.stepProgressBarCompletedBg ?? 'success.500';
        const inactiveBg = ss.stepProgressBarInactiveBg ?? 'background.level2';
        const circleWidth = ss.stepProgressBarCircleWidth ?? 32;
        const circleHeight = ss.stepProgressBarCircleHeight ?? 32;
        const circleGap = ss.stepProgressBarCircleGap ?? 1;
        const lineHeight = ss.stepProgressBarLineHeight ?? '2px';
        const lineWidth = ss.stepProgressBarLineWidth;
        const completedLineColor = ss.stepProgressBarCompletedLineColor ?? 'success.500';
        const inactiveLineColor = ss.stepProgressBarInactiveLineColor ?? 'background.level2';
        const hideNumbers = ss.stepProgressBarHideNumbers === true || ss.stepProgressBarHideNumbers === 'true';
        const align = ss.stepProgressBarAlign ?? 'left';
        const barAlignSx =
          align === 'center'
            ? { width: 'fit-content' as const, mx: 'auto' }
            : align === 'right'
            ? { width: 'fit-content' as const, ml: 'auto' }
            : {};

        const hiddenStepIndices = (element.styles?.hiddenStepIndices as number[] | undefined) || [];
        const visibleSteps = sortedSteps
          .map((step, idx) => ({ step, originalIndex: idx }))
          .filter(
            ({ step, originalIndex }) =>
              !hiddenStepIndices.includes(originalIndex) &&
              isStepVisibleByConditions(step, transactionData),
          );

        if (visibleSteps.length === 0) {
          return (
            <Box key={index} sx={{ mb: 2, ...elementStyles }} {...getHtmlAttributes()}>
              <Typography level="body-sm" sx={{ color: 'text.tertiary', textAlign: 'center' }}>
                No steps to display
              </Typography>
            </Box>
          );
        }

        return (
          <Box key={index} sx={{ mb: 2, ...elementStyles }} {...getHtmlAttributes()}>
            <Box sx={{ display: 'flex', alignItems: 'center', gap: circleGap, mb: hideLabels ? 0 : 1, ...barAlignSx }}>
              {visibleSteps.map(({ step, originalIndex }, visibleIdx) => {
                const stepNumber = visibleIdx + 1;
                const isActive = originalIndex === currentStepIndex;
                const isCompleted = originalIndex < currentStepIndex;

                return (
                  <React.Fragment key={originalIndex}>
                    <Box
                      sx={{
                        flex: 1,
                        display: 'flex',
                        flexDirection: 'column',
                        alignItems: 'center',
                        gap: hideLabels ? 0 : 0.5,
                      }}
                    >
                      <Box
                        sx={{
                          width: circleWidth,
                          height: circleHeight,
                          borderRadius: '50%',
                          display: 'flex',
                          alignItems: 'center',
                          justifyContent: 'center',
                          backgroundColor: isActive ? activeBg : isCompleted ? completedBg : inactiveBg,
                          color: isActive || isCompleted ? 'white' : 'text.secondary',
                          fontWeight: 'md',
                          fontSize: '0.875rem',
                          transition: 'all 0.2s',
                        }}
                      >
                        {hideNumbers ? (isCompleted ? '✓' : '') : (isCompleted ? '✓' : stepNumber)}
                      </Box>
                      {!hideLabels && (
                        <Typography
                          level="body-xs"
                          sx={{
                            textAlign: 'center',
                            color: isActive ? 'primary.600' : 'text.secondary',
                            fontWeight: isActive ? 'md' : 'normal',
                            maxWidth: '100px',
                            overflow: 'hidden',
                            textOverflow: 'ellipsis',
                            whiteSpace: 'nowrap',
                          }}
                        >
                          {step.title}
                        </Typography>
                      )}
                    </Box>
                    {visibleIdx < visibleSteps.length - 1 && (
                      <Box
                        sx={{
                          flex: 1,
                          height: lineHeight,
                          ...(lineWidth ? { minWidth: lineWidth } : {}),
                          backgroundColor: originalIndex < currentStepIndex ? completedLineColor : inactiveLineColor,
                          transition: 'all 0.2s',
                        }}
                      />
                    )}
                  </React.Fragment>
                );
              })}
            </Box>
            {!hideLabels && (
              <Typography level="body-xs" sx={{ textAlign: 'center', color: 'text.tertiary', mt: 1 }}>
                Step {currentStepNumber} of {totalSteps}
              </Typography>
            )}
          </Box>
        );
      }
      default:
        return null;
    }
  };

  // Inject custom CSS and JS into head
  useEffect(() => {
    const existingStyle = document.getElementById('form-custom-css');
    if (existingStyle) existingStyle.remove();
    document.querySelectorAll('[id^="form-custom-js"]').forEach((el) => el.remove());

    if (customCss && customCss.trim()) {
      const tempDiv = document.createElement('div');
      tempDiv.innerHTML = customCss;
      const styleElements = tempDiv.querySelectorAll('style');
      
      if (styleElements.length > 0) {
        styleElements.forEach((styleEl) => {
          const clonedStyle = styleEl.cloneNode(true) as HTMLStyleElement;
          clonedStyle.id = 'form-custom-css';
          document.head.appendChild(clonedStyle);
        });
      } else {
        const styleTag = document.createElement('style');
        styleTag.id = 'form-custom-css';
        styleTag.textContent = customCss;
        document.head.appendChild(styleTag);
      }
    }

    // Fresh <script> nodes required — cloned scripts do not execute (HTML5).
    if (customJs && customJs.trim()) {
      const tempDiv = document.createElement('div');
      tempDiv.innerHTML = customJs;
      const scriptElements = tempDiv.querySelectorAll('script');

      if (scriptElements.length > 0) {
        scriptElements.forEach((scriptEl, idx) => {
          const newScript = document.createElement('script');
          newScript.id = idx === 0 ? 'form-custom-js' : `form-custom-js-${idx}`;
          newScript.textContent = scriptEl.textContent || '';
          // Preserve src scripts if present (rare for customJs field).
          if (scriptEl.src) {
            newScript.src = scriptEl.src;
          }
          document.head.appendChild(newScript);
        });
      } else {
        const scriptTag = document.createElement('script');
        scriptTag.id = 'form-custom-js';
        scriptTag.textContent = customJs;
        document.head.appendChild(scriptTag);
      }
    }

    return () => {
      const styleToRemove = document.getElementById('form-custom-css');
      if (styleToRemove) styleToRemove.remove();
      document.querySelectorAll('[id^="form-custom-js"]').forEach((el) => el.remove());
    };
  }, [customCss, customJs]);

  if (loading) {
    return (
      <Box
        sx={{
          display: 'flex',
          justifyContent: 'center',
          alignItems: 'center',
          minHeight: '100vh',
        }}
      >
        <CircularProgress />
      </Box>
    );
  }

  if (formNotFound || (isPublishedPath && publishedPath && !form && !loading)) {
    const message = formNotFound ? 'Form not found' : (transactionNotFound ? 'Transaction not found' : 'Form not found');
    return (
      <NotFound
        message={message}
        showHomeLink={!isPublishedPath}
      />
    );
  }

  if (paymentEventsMissingTransactionId || (isPublishedPath && transactionId && transactionNotFound)) {
    return (
      <NotFound
        message="Transaction not found"
        showHomeLink={false}
      />
    );
  }

  const bodyPaddingY = form?.styles?.bodyPaddingY ?? 4;
  const bodyPaddingX = form?.styles?.bodyPaddingX ?? 2;
  const bodyPaddingYMobile = form?.styles?.bodyPaddingYMobile;
  const bodyPaddingXMobile = form?.styles?.bodyPaddingXMobile;

  const pySx = bodyPaddingYMobile != null && bodyPaddingYMobile !== ''
    ? { xs: bodyPaddingYMobile, sm: bodyPaddingY }
    : bodyPaddingY;
  const pxSx = bodyPaddingXMobile != null && bodyPaddingXMobile !== ''
    ? { xs: bodyPaddingXMobile, sm: bodyPaddingX }
    : bodyPaddingX;

  return (
    <Box
      sx={{
        minHeight: '100vh',
        bgcolor: 'background.body',
        py: pySx,
        px: pxSx,
        ...getBodyStyles(),
      }}
    >
      {form && (
        <ToastContainer
          containerId="form-toast"
          position={formToastOptions.position ?? 'top-center'}
          autoClose={3000}
          icon={formToastOptions.hideIcon ? false : true}
          closeButton={
            formToastOptions.showCloseButton
              ? formToastOptions.closeButtonColor
                ? (props: CloseButtonProps) => (
                    <button
                      type="button"
                      aria-label="close"
                      onClick={(e) => props.closeToast(e)}
                      style={{
                        background: 'none',
                        border: 'none',
                        color: formToastOptions.closeButtonColor,
                        cursor: 'pointer',
                        fontSize: '1.2rem',
                        lineHeight: 1,
                        padding: 4,
                        opacity: 0.8,
                      }}
                    >
                      ×
                    </button>
                  )
                : true
              : false
          }
        />
      )}
      <Box
        sx={{
          maxWidth: form?.styles?.maxWidth || '800px',
          mx: 'auto',
        }}
      >
        <Card variant="plain" sx={{ border: 'none', boxShadow: 'none', p: 0, backgroundColor: 'transparent' }}>
          <CardContent sx={{ p: 0 }}>
            <Box
              component="form"
              ref={formRef}
              onSubmit={async (e) => {
                e.preventDefault();
                const newErrors: ValidationErrors = {};
                for (const [index, element] of elements.entries()) {
                  if (element.type === 'input' || element.type === 'select') {
                    const triggers = element.styles?.validationTriggers || [];
                    if (triggers.includes('onSubmit')) {
                      const fieldName = getFieldName(element, index);
                      const value = formValues[fieldName] || '';
                      const errors = await validateField(element, value, getValidateFieldOptions());
                      if (errors.length > 0) {
                        newErrors[fieldName] = errors;
                      }
                    }
                  }
                }
                setValidationErrors(newErrors);
              }}
              sx={{
                position: 'relative',
                ...getFormStyles(),
              }}
            >
              {(() => {
                const list =
                  steps && steps.length > 0 && viewMode === 'step_by_step'
                    ? elements.filter((el) => {
                        const currentStep = steps[currentStepIndex];
                        if (!currentStep) return false;
                        const stepId = currentStep.id ?? `temp_${currentStepIndex}`;
                        return el.stepId === currentStep.id || el.stepId === stepId || el.stepId === currentStepIndex;
                      })
                    : steps && steps.length > 0
                      ? steps.flatMap((s) => (s.elements ?? []))
                      : elements;
                const positionedTimers = list
                  .filter((el) => isElementVisibleByUrlParam(el) && isElementVisibleByVariable(el, transactionData) && !isElementHiddenByVariable(el, transactionData))
                  .filter(
                  (el) =>
                    el.type === 'timer' &&
                    (el.styles?.timerPosition === 'top-left' ||
                      el.styles?.timerPosition === 'top' ||
                      el.styles?.timerPosition === 'top-right'),
                );
                if (positionedTimers.length === 0) return null;
                const onExpiryNavigateToErrorStep =
                  viewMode === 'step_by_step' && steps && steps.length > 0 && form?.errorStepId != null
                    ? () => {
                        const idx = steps.findIndex((s) => s.id === form?.errorStepId);
                        if (idx >= 0) navigateToStep(idx);
                      }
                    : undefined;
                return (
                  <Box sx={{ position: 'absolute', top: 0, left: 0, right: 0, zIndex: 1, pointerEvents: 'none' }}>
                    {positionedTimers.map((el, i) => {
                      const linkId = el.styles?.timerOnExpiry === 'show_link' ? el.styles?.linkElementIdAfterExpiry : undefined;
                      if (linkId && revealedLinkIds.has(Number(linkId))) {
                        return null;
                      }
                      return (
                        <TimerElement
                          key={el.id ?? `timer-${i}`}
                          element={el}
                          componentStyles={componentStyles}
                          replaceVariables={replaceVariables}
                          transactionData={transactionData ?? undefined}
                          onExpiryNavigateToErrorStep={onExpiryNavigateToErrorStep}
                          onExpiryShowLink={linkId ? (id) => setRevealedLinkIds((prev) => new Set(prev).add(id)) : undefined}
                        />
                      );
                    })}
                  </Box>
                );
              })()}
              {formName && !shouldHideFormName && (
                <Typography level="h2" sx={{ mb: 1, textAlign: 'center' }}>
                  {formName}
                </Typography>
              )}
              
              {formDescription && (
                <Typography level="body-lg" sx={{ mb: 4, textAlign: 'center', color: 'text.secondary' }}>
                  {formDescription}
                </Typography>
              )}

              {(() => {
                if (steps && steps.length > 0) {
                  // Render with steps
                  const sortedSteps = [...steps].sort((a, b) => (a.order ?? 0) - (b.order ?? 0));
                  
                  if (sortedSteps.length === 0) {
                    return (
                      <Box
                        sx={{
                          py: 4,
                          textAlign: 'center',
                          color: 'text.tertiary',
                        }}
                      >
                        <Typography level="body-sm">
                          No steps in this form
                        </Typography>
                      </Box>
                    );
                  }

                  // Step-by-step mode: show only current step
                  if (viewMode === 'step_by_step') {
                    const currentStep = sortedSteps[currentStepIndex];
                    if (!currentStep) {
                      return (
                        <Box sx={{ py: 4, textAlign: 'center', color: 'text.tertiary' }}>
                          <Typography level="body-sm">No step available</Typography>
                        </Box>
                      );
                    }

                    const stepId = currentStep.id || `temp_${currentStepIndex}`;
                    const stepElements = elements
                      .filter((el) => {
                        if (currentStep.id) {
                          return el.stepId === currentStep.id;
                        }
                        return el.stepId === stepId || el.stepId === currentStepIndex;
                      })
                      .filter((el) => !el.styles?.hidden && isElementVisibleByUrlParam(el) && isElementVisibleByVariable(el, transactionData) && !isElementHiddenByVariable(el, transactionData))
                      .sort((a, b) => (a.order ?? 0) - (b.order ?? 0));

                    const animationStyles = {
                      fade: {
                        opacity: isTransitioning ? 0 : 1,
                        transition: 'opacity 0.3s ease-in-out',
                      },
                      slide: {
                        transform: isTransitioning ? 'translateX(100%)' : 'translateX(0)',
                        opacity: isTransitioning ? 0 : 1,
                        transition: 'transform 0.3s ease-in-out, opacity 0.3s ease-in-out',
                      },
                      none: {},
                    };

                    const handleBackClick = () => {
                      for (let i = currentStepIndex - 1; i >= 0; i--) {
                        if (isStepVisibleByConditions(sortedSteps[i], transactionData)) {
                          navigateToStep(i);
                          return;
                        }
                      }
                    };

                    return (
                      <Box
                        sx={{
                          ...animationStyles[stepAnimation],
                          position: 'relative',
                          ...getStepStyles(currentStep),
                        }}
                      >
                        {viewMode === 'step_by_step' &&
                          formStyles.showBackButton &&
                          sortedSteps
                            .slice(0, currentStepIndex)
                            .some((s) => isStepVisibleByConditions(s, transactionData)) && (
                          <Button
                            variant="outlined"
                            size="sm"
                            onClick={handleBackClick}
                            sx={{ mb: 2 }}
                            startDecorator={
                              formStyles.backButtonIcon ? (
                                <Box
                                  sx={{
                                    width: 16,
                                    height: 16,
                                    display: 'flex',
                                    alignItems: 'center',
                                    justifyContent: 'center',
                                  }}
                                  dangerouslySetInnerHTML={{ __html: sanitizeSvg(formStyles.backButtonIcon) }}
                                />
                              ) : (
                                <ArrowBack sx={{ fontSize: 16 }} />
                              )
                            }
                          >
                            {formStyles.backButtonText || 'Back'}
                          </Button>
                        )}
                        <Typography level="title-lg" sx={{ mb: 1 }}>
                          {currentStep.title}
                        </Typography>
                        {currentStep.description && (
                          <Typography level="body-sm" sx={{ mb: 2, color: 'text.secondary' }}>
                            {currentStep.description}
                          </Typography>
                        )}
                        <Stack spacing={0}>
                          {stepElements.length === 0 ? (
                            <Typography level="body-sm" sx={{ color: 'text.tertiary', fontStyle: 'italic' }}>
                              No elements in this step
                            </Typography>
                          ) : (
                            stepElements.map((element) => {
                              const globalIndex = elements.findIndex((el) => el === element);
                              const elementKey = element.id || `element_${globalIndex >= 0 ? globalIndex : stepElements.indexOf(element)}`;
                              return (
                                <Box key={elementKey}>
                                  {renderElement(element, globalIndex >= 0 ? globalIndex : 0, componentStyles)}
                                </Box>
                              );
                            })
                          )}
                        </Stack>
                      </Box>
                    );
                  }

                  // Single page mode: show steps that pass visibility conditions
                  const visibleSortedSteps = sortedSteps.filter((step) =>
                    isStepVisibleByConditions(step, transactionData),
                  );
                  return (
                    <Stack spacing={3}>
                      {visibleSortedSteps.map((step, stepIndex) => {
                        // Match elements to this step
                        const originalIndex = sortedSteps.indexOf(step);
                        const stepId = step.id || `temp_${originalIndex}`;
                        const stepKey = step.id || `step_${originalIndex}`;
                        const stepElements = elements
                          .filter((el) => {
                            if (step.id) {
                              return el.stepId === step.id;
                            }
                            return el.stepId === stepId || el.stepId === originalIndex;
                          })
                          .filter((el) => !el.styles?.hidden && isElementVisibleByUrlParam(el) && isElementVisibleByVariable(el, transactionData) && !isElementHiddenByVariable(el, transactionData))
                          .sort((a, b) => (a.order ?? 0) - (b.order ?? 0));
                        
                        return (
                          <Box key={stepKey} sx={getStepStyles(step)}>
                            {stepIndex > 0 && <Divider sx={{ my: 2 }} />}
                            <Typography level="title-lg" sx={{ mb: 1 }}>
                              {step.title}
                            </Typography>
                            {step.description && (
                              <Typography level="body-sm" sx={{ mb: 2, color: 'text.secondary' }}>
                                {step.description}
                              </Typography>
                            )}
                            <Stack spacing={0}>
                              {stepElements.length === 0 ? (
                                <Typography level="body-sm" sx={{ color: 'text.tertiary', fontStyle: 'italic' }}>
                                  No elements in this step
                                </Typography>
                              ) : (
                                stepElements.map((element) => {
                                  const globalIndex = elements.findIndex((el) => el === element);
                                  const elementKey = element.id || `element_${globalIndex >= 0 ? globalIndex : stepElements.indexOf(element)}`;
                                  return (
                                    <Box key={elementKey}>
                                      {renderElement(element, globalIndex >= 0 ? globalIndex : 0, componentStyles)}
                                    </Box>
                                  );
                                })
                              )}
                            </Stack>
                          </Box>
                        );
                      })}
                    </Stack>
                  );
                } else {
                  // Render without steps (backward compatibility)
                  const sortedElements = elements
                    .filter((el) => !el.stepId)
                    .filter((el) => !el.styles?.hidden && isElementVisibleByUrlParam(el) && isElementVisibleByVariable(el, transactionData) && !isElementHiddenByVariable(el, transactionData))
                    .sort((a, b) => (a.order ?? 0) - (b.order ?? 0));
                  
                  return sortedElements.length === 0 ? (
                    <Box
                      sx={{
                        py: 4,
                        textAlign: 'center',
                        color: 'text.tertiary',
                      }}
                    >
                      <Typography level="body-sm">
                        No elements in this form
                      </Typography>
                    </Box>
                  ) : (
                    <Stack spacing={0}>
                      {sortedElements.map((element, index) => {
                        const elementKey = element.id || `element_${index}`;
                        return (
                          <Box key={elementKey}>
                            {renderElement(element, index, componentStyles)}
                          </Box>
                        );
                      })}
                    </Stack>
                  );
                }
              })()}
            </Box>
          </CardContent>
        </Card>
      </Box>
    </Box>
  );
};

export default FormPreviewPage;
